import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Document, Page } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ArrowRightIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  SunIcon,
  MoonIcon,
  XMarkIcon,
  PaperAirplaneIcon,
  ArrowLeftIcon,
  ArrowPathIcon,
  SparklesIcon,
  PlusIcon,
  ScissorsIcon,
  PhotoIcon,
  PaperClipIcon,
  ChatBubbleLeftRightIcon,
  MagnifyingGlassPlusIcon,
  MagnifyingGlassMinusIcon,
  Bars3Icon,
  BookOpenIcon,
  BookmarkIcon,
  QuestionMarkCircleIcon
} from '@heroicons/react/24/outline';
import { BookmarkIcon as BookmarkSolidIcon, BoltIcon } from '@heroicons/react/24/solid';
import { api, API_BASE_URL } from '../config/api';
import { streamAgentChat } from '../config/api_agent';
import { useTheme } from '../contexts/ThemeContext';
import { useToast } from '../contexts/ToastContext';
import { useAuth, SupportedLanguage } from '../contexts/AuthContext';
import { LanguageToggle } from '../components/common/LanguageToggle';
import { useReadingNotes } from '../hooks/useReadingNotes';
import { useBookmarks } from '../hooks/useBookmarks';
import { useReadingSession } from '../hooks/useReadingSession';
import { useIsMobile } from '../hooks/useIsMobile';
import { generateSelectionGroupId } from '../utils/idGenerators';
import { fetchAndCachePDFWithProgress, cleanupExpiredCache, PDFLoadProgress } from '../utils/pdfCache';
import { trackEvent } from '../utils/analytics';
import { NoteType, ReadingNote } from '../types/reading-notes';
import { useChatSessions } from '../hooks/useChatSessions';
import { useChatMessages } from '../hooks/useChatMessages';
import { ChatSession, ChatMessage, MessageAttachment } from '../types/chat';
import { ThinkingIndicator } from '../components/ThinkingIndicator';
import { MarkdownRenderer } from '../components/MarkdownRenderer';
import { CollapsibleMarkdown } from '../components/chat/CollapsibleMarkdown';
import { parseMarkdownSections } from '../utils/markdownSectionParser';
import SparkleIcon from '../components/SparkleIcon';
import { S3Image } from '../components/S3Image';
import { Tooltip } from 'react-tooltip';
import { useDynamicPlaceholder } from '../hooks/useDynamicPlaceholder';
import { useSessionProgressToast } from '../hooks/useSessionProgressToast';
import { useOnboardingTour } from '../hooks/useOnboardingTour';
import { ChatContextualPrompts } from '../components/chat/ChatContextualPrompts';
import { SessionProgressToast } from '../components/chat/SessionProgressToast';
import { PulseCheckCard } from '../components/reader/PulseCheckCard';
import BookmarkPopover, { BOOKMARK_COLORS } from '../components/bookmarks/BookmarkPopover';
import MobileReaderModal from '../components/common/MobileReaderModal';

// Import pdfjs
import { pdfjs } from 'react-pdf';

// Use local worker file to avoid CORS issues
pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

interface BookData {
  id: number;
  title: string;
  author: string;
  book_url: string;
  max_page_reached: number;  // Furthest page (for progress bar)
  last_viewed_page: number;  // Resume point
  total_pages: number;
}

interface ResolvedOutlineItem {
  title: string;
  dest: any;
  items?: ResolvedOutlineItem[];
  pageNumber: number | null;
}

// Using ChatMessage from types/chat.ts instead of local Message interface
// MessageAttachment is also imported from types/chat.ts

// interface Annotation {
//   id: string;
//   pageNumber: number;
//   type: 'text' | 'snippet';
//   text?: string;
//   imageUrl?: string;
//   note?: string;
//   bounds: {
//     x: number;
//     y: number;
//     width: number;
//     height: number;
//   };
//   createdAt: string;
// }

// The next item in a course sequence, returned by the complete endpoint, used
// to render the "up next" handoff card.
interface CourseNextItem {
  id: string;
  item_type: 'reading' | 'assignment';
  book_id?: string | null;
  page_start?: number | null;
  page_end?: number | null;
  book_title?: string | null;
  book_cover_url?: string | null;
  assignment_title?: string | null;
}

const Dhyana: React.FC = () => {
  const { bookId } = useParams<{ bookId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Course context — present when a reading is opened from a course (My Courses).
  // Scopes the chat session to the course reading so it stays separate from
  // ad-hoc book reading (see useChatSessions + the session list filter).
  const courseId = searchParams.get('course');
  const courseItemId = searchParams.get('item');
  // The reading's end page (from the course deep-link). Reaching it marks the
  // reading complete and surfaces the "up next" handoff.
  const courseEnd = searchParams.get('end');
  // Phase 2 multilingual: read user's preferred language to drive loading-state
  // copy. Cache miss = real translation latency (~1-2s); cache hit = fast — the
  // message is shown briefly either way and communicates *why* there's a delay
  // for Indic users so they don't think the app is hung.
  const { user } = useAuth();
  const _LANG_DISPLAY: Record<string, string> = {
    hindi: 'हिन्दी',
    gujarati: 'ગુજરાતી',
    marathi: 'मराठी',
  };
  const userLangDisplay = user?.language && user.language !== 'english'
    ? _LANG_DISPLAY[user.language]
    : null;
  const { isDarkMode, toggleDarkMode } = useTheme();
  const { showToast } = useToast();
  const { isMobile, isTablet } = useIsMobile();

  // Book and PDF state
  const [book, setBook] = useState<BookData | null>(null);
  const [numPages, setNumPages] = useState<number>(0);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [loading, setLoading] = useState(true);

  // Course "up next" handoff. `courseUpNext` is set once the student reaches the
  // reading's end page (null payload `next` = course finished). `upNextOpen`
  // drives the slide-in; the ref guards against re-firing the completion call.
  const [courseUpNext, setCourseUpNext] = useState<{ next: CourseNextItem | null } | null>(null);
  const [upNextOpen, setUpNextOpen] = useState(false);
  // Holds the course item id we've already fired completion for — keying by id
  // makes the guard self-reset per reading and survive StrictMode's double-run.
  const courseCompleteFiredRef = useRef<string | null>(null);
  const [loadProgress, setLoadProgress] = useState<PDFLoadProgress | null>(null);
  const [error, setError] = useState<string>('');

  // Playful loading messages - progress-based (Playful & Whimsical)
  const loadingMessages = [
    { message: "Summoning your book from the shelves...", subMessage: null },  // 0-25%
    { message: "The pages are flying in...", subMessage: null },               // 25-50%
    { message: "Brewing some reading magic...", subMessage: null },            // 50-75%
    { message: "Almost ready to dive in...", subMessage: null }                // 75-100%
  ];

  const [pdfUrl, setPdfUrl] = useState<string>('');
  const [pdfKey, setPdfKey] = useState<number>(0); // Force re-render of Document

  // Reading notes hook - integrate with backend
  const bookIdStr = bookId || '';
  const {
    notes: pageNotes,
    loading: notesLoading,
    fetchNotesForPage,
    createNote,
    createMultiPageNotes,
    updateNote,
    deleteNote,
    getIndexingStatus,
    updateNoteIndexingStatus
  } = useReadingNotes(bookIdStr);

  // Bookmarks hook
  const {
    bookmarks,
    isPageBookmarked,
    getBookmarkForPage,
    createBookmark,
    updateBookmark,
    deleteBookmark,
    loading: bookmarksLoading
  } = useBookmarks(bookIdStr);

  // Reading session tracking hook (for analytics)
  // Only start session when book is loaded (isReady=true) to prevent sessions starting at page 1
  const { sessionId, isActive: sessionActive, pagesVisited } = useReadingSession(bookIdStr, pageNumber, !!book && !loading);

  // Chat sessions hook
  const {
    sessions: chatSessions,
    activeSessionId,
    loading: sessionsLoading,
    createSession,
    updateSession,
    updateSessionTitle,
    activateSession,
    getActiveSession
  } = useChatSessions(bookIdStr, courseId, courseItemId);

  // Chat messages hook
  const {
    messages,
    loading: messagesLoading,
    loadingMore,
    hasMore,
    sendMessage,
    loadMoreMessages,
    addLocalMessage,
    setMessages,
    // New functions for background streaming support
    updateCacheForSession,
    addMessageToCache,
    sessionIdRef,
    messagesCacheRef
  } = useChatMessages({
    bookId: bookIdStr,
    sessionId: activeSessionId
  });

  // Ref for real-time access to activeSessionId (for session-aware streaming)
  const activeSessionIdRef = useRef<string | null>(null);

  // Update ref SYNCHRONOUSLY during render (not in useEffect!)
  // This ensures the ref is always up-to-date before any child effects or callbacks run
  // Using useEffect here causes a race condition where child hook effects run first
  activeSessionIdRef.current = activeSessionId;
  
  // UI state
  const [activeTab, setActiveTab] = useState<'chat' | 'thoughtpad'>('chat');
  const [showSummary, setShowSummary] = useState(false);
  const [isThinking, setIsThinking] = useState(false);
  const [agentStatus, setAgentStatus] = useState<string>(''); // Agent tool progress
  const [streamingMessageId, setStreamingMessageId] = useState<string | null>(null);
  const [streamingSessionId, setStreamingSessionId] = useState<string | null>(null);
  const [isPulseActive, setIsPulseActive] = useState(false); // Locks chat during Pulse Check
  const [manualPulseCheck, setManualPulseCheck] = useState(false); // User-triggered Pulse Check via button
  const [summaryHeight, setSummaryHeight] = useState(40); // percentage
  const [tldrData, setTldrData] = useState<any>(null);
  const [tldrLoading, setTldrLoading] = useState(false);
  const [tldrCache, setTldrCache] = useState<Map<number, any>>(new Map());
  // EN-toggle override for TLDR. null = use user.language; 'english' = force English.
  // Per-surface state (doesn't affect Pulse / Playbook / etc.)
  const [tldrLangOverride, setTldrLangOverride] = useState<SupportedLanguage | null>(null);
  const [isResizing, setIsResizing] = useState(false);
  const [sidebarWidth, setSidebarWidth] = useState<number>(550); // px
  const [isResizingSidebar, setIsResizingSidebar] = useState(false);
  const [showPageIndicator, setShowPageIndicator] = useState(false);
  const [pageInputValue, setPageInputValue] = useState<string>('1');
  const [showScrollHint, setShowScrollHint] = useState(false);
  const [showKeyboardHelp, setShowKeyboardHelp] = useState(false);

  // Chat state
  const [chatInput, setChatInput] = useState('');
  const [showSessionMenu, setShowSessionMenu] = useState(false);

  // Dynamic placeholder hook for contextual chat prompts
  const isUserTyping = chatInput.length > 0;
  const { placeholder: dynamicPlaceholder, currentConcept } = useDynamicPlaceholder({
    currentPage: pageNumber,
    pagesVisited,
    tldrCache,
    currentTldrData: tldrData,
    isUserTyping,
    isReady: !!book && !loading
  });

  // Session progress toast hook (20min or 10 pages trigger)
  const {
    showToast: showProgressToast,
    toastConcepts,
    onRecap: handleToastRecap,
    onConnect: handleToastConnect,
    onDismiss: handleToastDismiss
  } = useSessionProgressToast({
    pagesVisited,
    tldrCache,
    isReady: !!book && !loading,
    currentPage: pageNumber
  });

  // Onboarding tour hook (auto-triggers on first visit)
  const { startTour, hasCompletedTour } = useOnboardingTour({
    isReady: !!book && !loading && numPages > 0,
    onSwitchToThoughtPad: () => setActiveTab('thoughtpad'),
    onOpenSidebar: () => setIsSidebarVisible(true),
    onSwitchToChat: () => setActiveTab('chat')
  });

  // Highlight help button + auto-show tooltip for users who haven't completed tour
  const [showHelpHighlight, setShowHelpHighlight] = useState(false);
  const [showTourTooltip, setShowTourTooltip] = useState(false);
  useEffect(() => {
    if (!!book && !loading && !hasCompletedTour) {
      setShowHelpHighlight(true);
      // Delay tooltip slightly so the button renders first
      const tooltipTimer = setTimeout(() => setShowTourTooltip(true), 500);
      const hideTimer = setTimeout(() => {
        setShowHelpHighlight(false);
        setShowTourTooltip(false);
      }, 20000); // 20 seconds
      return () => { clearTimeout(tooltipTimer); clearTimeout(hideTimer); };
    }
  }, [book, loading, hasCompletedTour]);

  // Current session helper
  const currentSession = getActiveSession();
  
  // Snipping tool state
  const [isSnipping, setIsSnipping] = useState(false);
  const [snipStart, setSnipStart] = useState<{ x: number; y: number } | null>(null);
  const [snipEnd, setSnipEnd] = useState<{ x: number; y: number } | null>(null);
  const [attachments, setAttachments] = useState<MessageAttachment[]>([]);
  const [hoveredAttachment, setHoveredAttachment] = useState<string | null>(null);
  
  // Sidebar visibility
  const [isSidebarVisible, setIsSidebarVisible] = useState(true);
  const [zoomLevel, setZoomLevel] = useState(80); // Zoom percentage
  
  // PDF Outline state
  const [resolvedOutline, setResolvedOutline] = useState<ResolvedOutlineItem[]>([]);
  const [showOutline, setShowOutline] = useState(false);
  const [outlineWidth, setOutlineWidth] = useState(250); // px
  const [isResizingOutline, setIsResizingOutline] = useState(false);
  const [bookmarksWidth, setBookmarksWidth] = useState(250); // px
  const [isResizingBookmarks, setIsResizingBookmarks] = useState(false);
  const [pdfDocument, setPdfDocument] = useState<any>(null); // Store PDF document reference

  // Bookmarks sidebar state
  const [showBookmarks, setShowBookmarks] = useState(false);
  
  // ThoughtPad input state
  const [thoughtpadInput, setThoughtpadInput] = useState('');
  const [thoughtpadAttachments, setThoughtpadAttachments] = useState<MessageAttachment[]>([]);
  // const [pageAnnotations, setPageAnnotations] = useState<any[]>([]);
  // const [hoveredAnnotation, setHoveredAnnotation] = useState<string | null>(null);
  
  // Add Note modal state
  const [showAddNoteModal, setShowAddNoteModal] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [noteAttachments, setNoteAttachments] = useState<MessageAttachment[]>([]);
  
  // Multi-page selection state
  const [isMultiPageSelecting, setIsMultiPageSelecting] = useState(false);
  const [multiPageSelections, setMultiPageSelections] = useState<{
    pageNumber: number;
    text: string;
  }[]>([]);
  
  // Note viewer modal state
  const [showNoteViewerModal, setShowNoteViewerModal] = useState(false);
  const [viewingNote, setViewingNote] = useState<ReadingNote | null>(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [expandedNotes, setExpandedNotes] = useState<Set<string>>(new Set());
  
  // Chat message viewer modal state
  const [showMessageViewerModal, setShowMessageViewerModal] = useState(false);
  const [viewingMessage, setViewingMessage] = useState<ChatMessage | null>(null);
  const [currentMessageImageIndex, setCurrentMessageImageIndex] = useState(0);
  const [expandedMessages, setExpandedMessages] = useState<Set<string>>(new Set());
  const [expandedTextMessages, setExpandedTextMessages] = useState<Set<string>>(new Set());
  const [zoomedImageUrl, setZoomedImageUrl] = useState<string | null>(null);
  
  // Page navigation with return button state
  const [previousPage, setPreviousPage] = useState<number | null>(null);
  const [showReturnButton, setShowReturnButton] = useState(false);
  const returnButtonTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // Return to Chat state
  const [returnToChat, setReturnToChat] = useState<{ sessionId?: string } | null>(null);
  
  
  // Refs
  const summaryRef = useRef<HTMLDivElement>(null);
  const chatAreaRef = useRef<HTMLDivElement>(null);
  const pdfContentRef = useRef<HTMLDivElement>(null);
  const isRequestInFlight = useRef<boolean>(false); // Prevent duplicate requests
  const abortControllerRef = useRef<AbortController | null>(null); // For request cancellation
  const chatInputRef = useRef<HTMLTextAreaElement>(null); // Auto-expand chat input

  // Debounced progress update - saves after 1 second of no page changes
  const updateProgressRef = useRef<NodeJS.Timeout | null>(null);
  const lastSavedPageRef = useRef<number>(0);

  const updateProgress = useCallback((page: number) => {
    // Clear any pending save
    if (updateProgressRef.current) {
      clearTimeout(updateProgressRef.current);
    }

    // Debounce the save by 1 second
    updateProgressRef.current = setTimeout(async () => {
      try {
        await api.put(`/api/books/${bookId}/progress`, {
          current_page: page
        });
        lastSavedPageRef.current = page;
      } catch (err) {
        console.error('Failed to update reading progress:', err);
      }
    }, 1000);
  }, [bookId]);

  // Immediate progress save (for beforeunload)
  const saveProgressImmediately = useCallback((page: number) => {
    // Use sendBeacon for reliable exit save
    const url = `${API_BASE_URL}/api/books/${bookId}/progress`;
    const data = JSON.stringify({ current_page: page });
    navigator.sendBeacon(url, data);
  }, [bookId]);

  // Save progress on page unload/close
  useEffect(() => {
    const handleBeforeUnload = () => {
      // Clear any pending debounced save
      if (updateProgressRef.current) {
        clearTimeout(updateProgressRef.current);
      }
      // Save immediately using sendBeacon
      if (pageNumber !== lastSavedPageRef.current) {
        saveProgressImmediately(pageNumber);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      // Also save on component unmount
      if (updateProgressRef.current) {
        clearTimeout(updateProgressRef.current);
      }
    };
  }, [bookId, pageNumber, saveProgressImmediately]);

  // Cleanup expired PDF cache on mount
  useEffect(() => {
    cleanupExpiredCache().catch(console.error);
  }, []);

  // Check for returnToChat on mount
  useEffect(() => {
    const stored = sessionStorage.getItem('returnToChat');
    if (stored) {
      try {
        const chatInfo = JSON.parse(stored);
        setReturnToChat(chatInfo);
      } catch {
        // Invalid JSON, ignore
      }
    }
  }, []);

  // Fetch book data
  useEffect(() => {
    let isMounted = true;
    
    const loadBook = async () => {
      if (isMounted) {
        await fetchBook();
      }
    };
    
    loadBook();
    
    // Cleanup on unmount
    return () => {
      isMounted = false;
      // Revoke blob URL to prevent memory leak
      setPdfUrl(prev => {
        if (prev && prev.startsWith('blob:')) {
          URL.revokeObjectURL(prev);
        }
        return '';
      });
      setNumPages(0);
    };
  }, [bookId]);

  // Update reading progress when page changes (debounced)
  useEffect(() => {
    if (book && pageNumber > 0) {
      updateProgress(pageNumber);
    }
    setPageInputValue(pageNumber.toString());

    // Show page indicator briefly
    setShowPageIndicator(true);
    const timer = setTimeout(() => setShowPageIndicator(false), 1500);
    return () => clearTimeout(timer);
  }, [pageNumber, book, updateProgress]);

  // Moving to a different course reading clears the previous "up next" card and
  // jumps to the new reading's start page. The book-load effect only re-runs on
  // bookId change, so when the next reading is a different page range of the SAME
  // book (a textbook split into several readings), this is what actually advances
  // the page — without it, tapping "Continue" appears to do nothing.
  useEffect(() => {
    setCourseUpNext(null);
    setUpNextOpen(false);
    if (!courseItemId) return;
    const urlPage = searchParams.get('page');
    if (urlPage) {
      const parsed = parseInt(urlPage, 10);
      if (!isNaN(parsed) && parsed > 0) setPageNumber(parsed);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseItemId]);

  // Course reading completion: when the student reaches the reading's end page,
  // mark the item complete (once) and surface the next item. Best-effort — a
  // failed call never disrupts reading.
  useEffect(() => {
    const end = courseEnd ? parseInt(courseEnd, 10) : NaN;
    if (!courseId || !courseItemId || !Number.isFinite(end)) return;
    if (!book || loading || pageNumber < end) return;
    if (courseCompleteFiredRef.current === courseItemId) return;
    courseCompleteFiredRef.current = courseItemId;
    (async () => {
      try {
        const res = await api.post(`/api/courses/${courseId}/items/${courseItemId}/complete`);
        setCourseUpNext({ next: res.data?.next_item ?? null });
        setUpNextOpen(true);
      } catch (err) {
        console.error('Failed to mark course reading complete', err);
      }
    })();
  }, [pageNumber, courseEnd, courseId, courseItemId, book, loading]);

  // Fetch notes for current page when page changes
  useEffect(() => {
    if (bookIdStr && pageNumber > 0) {
      fetchNotesForPage(pageNumber);
    }
  }, [bookIdStr, pageNumber, fetchNotesForPage]);

  // Poll for indexing status updates (for notes with pending/running status)
  useEffect(() => {
    // Check if there are any notes currently being indexed
    const hasInProgressNotes = pageNotes?.some(note =>
      note.indexing_status === 'pending' || note.indexing_status === 'running'
    );

    // Only poll if there are in-progress notes
    if (!hasInProgressNotes || !bookIdStr || pageNumber <= 0) {
      return; // No polling needed
    }

    // Poll every 3 seconds to check for status updates
    const pollInterval = setInterval(() => {
      fetchNotesForPage(pageNumber);
    }, 3000);

    // Cleanup interval when component unmounts or dependencies change
    return () => clearInterval(pollInterval);
  }, [pageNotes, bookIdStr, pageNumber, fetchNotesForPage]);

  // Scroll to bottom of chat when new messages arrive
  useEffect(() => {
    if (chatAreaRef.current) {
      chatAreaRef.current.scrollTop = chatAreaRef.current.scrollHeight;
    }
  }, [messages]);

  // Auto-scroll during streaming to keep latest content visible
  useEffect(() => {
    if (streamingMessageId && chatAreaRef.current) {
      const scrollToBottom = () => {
        if (chatAreaRef.current) {
          chatAreaRef.current.scrollTop = chatAreaRef.current.scrollHeight;
        }
      };

      // Scroll immediately and set up interval for continuous scrolling
      scrollToBottom();
      const scrollInterval = setInterval(scrollToBottom, 100);

      return () => clearInterval(scrollInterval);
    }
  }, [streamingMessageId, messages]);

  // Auto-expand chat input textarea as content grows
  useEffect(() => {
    if (chatInputRef.current) {
      // Reset height to auto to get the correct scrollHeight
      chatInputRef.current.style.height = 'auto';
      // Set height based on content, with min and max constraints
      const scrollHeight = chatInputRef.current.scrollHeight;
      const minHeight = 120; // ~5 rows
      const maxHeight = 300; // Max expansion
      chatInputRef.current.style.height = `${Math.min(Math.max(scrollHeight, minHeight), maxHeight)}px`;

      // Scroll to bottom to show newly appended text (e.g., from PDF selection)
      // Only auto-scroll if textarea doesn't have focus (programmatic update)
      if (document.activeElement !== chatInputRef.current) {
        chatInputRef.current.scrollTop = chatInputRef.current.scrollHeight;
      }
    }
  }, [chatInput]);

  // Handle clicks outside menus
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('.session-menu')) {
        setShowSessionMenu(false);
      }
    };
    if (showSessionMenu) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [showSessionMenu]);

  const fetchBook = async () => {
    try {
      setLoading(true);
      setLoadProgress(null); // Reset progress
      setError(''); // Clear any previous errors
      const response = await api.get(`/api/books/${bookId}`);
      const bookData = response.data;
      setBook(bookData);

      // Check if there's a page query parameter in the URL
      const urlPage = searchParams.get('page');
      if (urlPage) {
        const parsedPage = parseInt(urlPage, 10);
        if (!isNaN(parsedPage) && parsedPage > 0) {
          // Use page from URL (e.g., when coming from note view)
          setPageNumber(parsedPage);
        } else {
          // Invalid page param, use last_viewed_page
          setPageNumber(bookData.last_viewed_page || 1);
        }
      } else {
        // No page param, use last_viewed_page for resume
        setPageNumber(bookData.last_viewed_page || 1);
      }

      // Load PDF with caching and progress tracking
      const originalUrl = bookData.book_url || `${API_BASE_URL}/api/books/${bookId}/file`;
      try {
        const cachedUrl = await fetchAndCachePDFWithProgress(
          bookIdStr,
          originalUrl,
          (progress) => setLoadProgress(progress)
        );
        setPdfUrl(cachedUrl);
        setPdfKey(prev => prev + 1);
      } catch (cacheError) {
        console.error('PDF cache error, falling back to direct URL:', cacheError);
        setPdfUrl(originalUrl);
        setPdfKey(prev => prev + 1);
      }
    } catch (err: any) {
      if (err.response?.status === 403) {
        // User doesn't have access — redirect to library
        navigate('/library', { replace: true });
        return;
      }
      setError(err.response?.data?.detail || 'Failed to load book');
      setNumPages(0);
      setPdfUrl('');
    } finally {
      setLoading(false);
    }
  };

  const fetchTLDR = async (forceRefresh: boolean = false) => {
    if (!bookId || !pageNumber) return;

    // Check cache first (unless forcing refresh)
    if (!forceRefresh && tldrCache.has(pageNumber)) {
      setTldrData(tldrCache.get(pageNumber));
      return;
    }

    try {
      setTldrLoading(true);
      // Pass reading_session_id so translation cost rows are grouped per reader session.
      // Pass lang= override when the EN toggle is active (otherwise backend uses
      // current_user.language automatically).
      const tldrParams = new URLSearchParams();
      if (sessionId) tldrParams.set('reading_session_id', sessionId);
      if (tldrLangOverride) tldrParams.set('lang', tldrLangOverride);
      const tldrQs = tldrParams.toString();
      const response = await api.get(
        `/api/books/${bookId}/pages/${pageNumber}/tldr${tldrQs ? `?${tldrQs}` : ''}`
      );
      setTldrData(response.data);

      // Cache the result
      setTldrCache(prev => new Map(prev).set(pageNumber, response.data));
    } catch (err: any) {
      console.error('Failed to fetch TLDR:', err);
      setTldrData(null);
    } finally {
      setTldrLoading(false);
    }
  };

  // Clear cache when changing books OR when the EN toggle override flips.
  // The per-page cache stores ONE rendition per page (in current lang); flipping
  // the override means the cached entry is for the wrong language → invalidate
  // and refetch.
  useEffect(() => {
    setTldrCache(new Map());
    setTldrData(null);
  }, [bookId, tldrLangOverride]);

  // Re-fetch the current page's TLDR when the override changes so the open
  // modal updates immediately instead of waiting for the next page navigation.
  // Force-refresh (true) bypasses the per-page tldrCache — the cache-clear
  // effect above and this fetch effect both fire on the SAME render cycle,
  // so the in-closure tldrCache here is still the OLD map (React batches
  // setState; effects close over the current render's values). Without
  // forceRefresh, this would cache-hit on the stale Gujarati entry and
  // skip the network call entirely when toggling to English.
  useEffect(() => {
    if (pageNumber && book && !loading) {
      fetchTLDR(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tldrLangOverride]);

  // Fetch TLDR on every page change (for dynamic placeholder and contextual prompts)
  // Cache handles deduplication, so this won't cause extra API calls
  useEffect(() => {
    if (pageNumber && book && !loading) {
      fetchTLDR();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNumber, book, loading]);

  // Also fetch when modal opens (for refresh functionality)
  useEffect(() => {
    if (showSummary && pageNumber) {
      fetchTLDR();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSummary, pageNumber]);

  const onDocumentLoadSuccess = async (pdf: any) => {
    const numPages = pdf.numPages;
    setNumPages(numPages);
    setError('');
    setPdfDocument(pdf); // Store PDF document reference for outline navigation
    
    // Extract PDF outline/table of contents and resolve page numbers for highlighting
    try {
      const outline = await pdf.getOutline();
      if (outline && outline.length > 0) {
        const resolved = await resolveOutlinePageNumbers(outline, pdf);
        setResolvedOutline(resolved);
      }
    } catch (err) {
      console.log('Could not extract PDF outline:', err);
    }
    
    // Show scroll hint after PDF loads
    if (numPages > 1) {
      setShowScrollHint(true);
      const timer = setTimeout(() => setShowScrollHint(false), 5000);
      return () => clearTimeout(timer);
    }
  };

  const onDocumentLoadError = (error: Error) => {
    console.error('PDF load error:', error);
    setError('Failed to load PDF. Please try again.');
    // Reset state on error
    setNumPages(0);
    setPageNumber(1);
  };
  
  // Navigate to an outline item's destination
  const handleOutlineClick = async (item: any) => {
    if (!item.dest || !pdfDocument) return;
    
    try {
      let pageNumber = null;
      
      // The destination could be a page number or a named destination
      if (typeof item.dest === 'string') {
        // Named destination - need to resolve it
        const destination = await pdfDocument.getDestination(item.dest);
        if (destination && destination[0]) {
          const pageRef = destination[0];
          // Get the page index from the reference
          const pageIndex = await pdfDocument.getPageIndex(pageRef);
          pageNumber = pageIndex + 1; // Convert to 1-indexed
        }
      } else if (Array.isArray(item.dest) && item.dest[0]) {
        // Direct destination array with page reference
        const pageRef = item.dest[0];
        
        if (typeof pageRef === 'number') {
          // Direct page number (rare but possible)
          pageNumber = pageRef + 1;
        } else if (pageRef && typeof pageRef === 'object') {
          // Page reference object - need to resolve it
          try {
            const pageIndex = await pdfDocument.getPageIndex(pageRef);
            pageNumber = pageIndex + 1; // Convert to 1-indexed
          } catch (err) {
            console.error('Could not resolve page reference:', err);
          }
        }
      }
      
      if (pageNumber && pageNumber > 0 && pageNumber <= numPages) {
        setPageNumber(pageNumber);
        setShowPageIndicator(true);
        setTimeout(() => setShowPageIndicator(false), 1500);
      }
    } catch (err) {
      console.error('Error navigating to outline item:', err);
    }
  };

  // Resolve all outline item destinations to page numbers
  const resolveOutlinePageNumbers = useCallback(async (
    items: any[],
    pdf: any
  ): Promise<ResolvedOutlineItem[]> => {
    const resolved: ResolvedOutlineItem[] = [];

    for (const item of items) {
      let itemPageNumber: number | null = null;

      if (item.dest && pdf) {
        try {
          if (typeof item.dest === 'string') {
            const destination = await pdf.getDestination(item.dest);
            if (destination && destination[0]) {
              const pageRef = destination[0];
              const pageIndex = await pdf.getPageIndex(pageRef);
              itemPageNumber = pageIndex + 1;
            }
          } else if (Array.isArray(item.dest) && item.dest[0]) {
            const pageRef = item.dest[0];
            if (typeof pageRef === 'number') {
              itemPageNumber = pageRef + 1;
            } else if (pageRef && typeof pageRef === 'object') {
              const pageIndex = await pdf.getPageIndex(pageRef);
              itemPageNumber = pageIndex + 1;
            }
          }
        } catch (err) {
          console.error('Error resolving outline destination:', err);
        }
      }

      const resolvedChildren = item.items && item.items.length > 0
        ? await resolveOutlinePageNumbers(item.items, pdf)
        : undefined;

      resolved.push({
        title: item.title,
        dest: item.dest,
        items: resolvedChildren,
        pageNumber: itemPageNumber
      });
    }

    return resolved;
  }, []);

  // Determine if an outline item is the current active section
  // upperBound constrains the range for last-sibling items (prevents Chapter 5's last item being active when in Chapter 6)
  const isOutlineItemActive = useCallback((
    item: ResolvedOutlineItem,
    siblings: ResolvedOutlineItem[],
    itemIndex: number,
    currentPage: number,
    upperBound: number
  ): boolean => {
    if (item.pageNumber === null) return false;

    // Find the next sibling with a valid page number
    let nextPageNumber: number | null = null;
    for (let i = itemIndex + 1; i < siblings.length; i++) {
      if (siblings[i].pageNumber !== null) {
        nextPageNumber = siblings[i].pageNumber;
        break;
      }
    }

    // If no next sibling, use the upperBound (parent's boundary, not end of document)
    const effectiveUpperBound = nextPageNumber !== null ? nextPageNumber : upperBound;

    return currentPage >= item.pageNumber && currentPage < effectiveUpperBound;
  }, []);

  // Recursive component to render outline items with active highlighting
  const OutlineItem: React.FC<{
    item: ResolvedOutlineItem;
    level: number;
    siblings: ResolvedOutlineItem[];
    itemIndex: number;
    upperBound: number;
  }> = ({ item, level, siblings, itemIndex, upperBound }) => {
    const [isExpanded, setIsExpanded] = useState(true);
    const hasChildren = item.items && item.items.length > 0;

    // Calculate this item's upper bound (next sibling's page or parent's upperBound)
    let itemUpperBound = upperBound;
    for (let i = itemIndex + 1; i < siblings.length; i++) {
      if (siblings[i].pageNumber !== null) {
        itemUpperBound = siblings[i].pageNumber!;
        break;
      }
    }

    // Determine if this item is the active section
    const isActive = isOutlineItemActive(item, siblings, itemIndex, pageNumber, upperBound);

    return (
      <div className="select-none">
        <div
          className={`flex items-center px-2 py-1 cursor-pointer rounded transition-colors ${
            isActive
              ? isDarkMode
                ? 'bg-cyan-900/30 hover:bg-cyan-900/40'
                : 'bg-cyan-50 hover:bg-cyan-100'
              : 'hover:bg-gray-100 dark:hover:bg-gray-800'
          }`}
          style={{ paddingLeft: `${level * 16 + 8}px` }}
          onClick={() => {
            if (hasChildren) {
              setIsExpanded(!isExpanded);
            }
            handleOutlineClick(item);
          }}
        >
          {hasChildren && (
            <button
              className="mr-1 p-0.5"
              onClick={(e) => {
                e.stopPropagation();
                setIsExpanded(!isExpanded);
              }}
            >
              {isExpanded ? (
                <ChevronDownIcon className="h-3 w-3 text-gray-500" />
              ) : (
                <ChevronRightIcon className="h-3 w-3 text-gray-500" />
              )}
            </button>
          )}
          <span className={`text-sm truncate ${
            isActive
              ? isDarkMode
                ? 'text-cyan-200 font-medium'
                : 'text-cyan-900 font-medium'
              : 'text-gray-700 dark:text-gray-300'
          }`}>
            {item.title}
          </span>
        </div>
        {hasChildren && isExpanded && (
          <div>
            {item.items!.map((child, index) => (
              <OutlineItem
                key={index}
                item={child}
                level={level + 1}
                siblings={item.items!}
                itemIndex={index}
                upperBound={itemUpperBound}
              />
            ))}
          </div>
        )}
      </div>
    );
  };

  const handleSummaryResize = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
    const startY = e.clientY;
    const startHeight = summaryHeight;

    const handleMouseMove = (e: MouseEvent) => {
      const viewerContainer = document.getElementById('viewer-container');
      if (!viewerContainer) return;
      
      const containerHeight = viewerContainer.offsetHeight;
      const deltaY = startY - e.clientY;
      const deltaPercentage = (deltaY / containerHeight) * 100;
      const newHeight = Math.min(80, Math.max(10, startHeight + deltaPercentage));
      setSummaryHeight(newHeight);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const handleSendMessage = async (overrideText?: string) => {
    if (!(overrideText || chatInput.trim()) && attachments.length === 0) return;
    if (!activeSessionId) return;

    // Prevent duplicate requests
    if (isRequestInFlight.current) {
      console.log('Request already in flight, ignoring duplicate call');
      return;
    }

    const messageText = overrideText || chatInput;
    const messageAttachments = [...attachments];
    
    // Keep original data_urls for agent (before S3 upload clears them)
    const attachmentsWithDataUrls = messageAttachments.map(att => ({
      data_url: att.data_url || '',
      source_page: att.page_number,
      mime_type: att.mime_type || 'image/png'
    }));

    // Clear input immediately
    setChatInput('');
    setAttachments([]);

    // Mark request as in-flight
    isRequestInFlight.current = true;

    // Variables accessible in both try and catch blocks
    let userMessageId: string | undefined;
    let markdownContent = '';
    let aiMessageId: string | undefined;

    // Capture the session ID for this streaming request (for session-aware updates)
    const currentStreamingSessionId = activeSessionId;
    setStreamingSessionId(activeSessionId);  // Set state so UI can conditionally render

    try {
      // Add user message to UI immediately (optimistic update)
      // Use a temporary ID that will be replaced when backend sends the real ID
      const tempUserMessageId = `temp-user-${Date.now()}-${Math.random()}`; // More unique temp ID
      userMessageId = tempUserMessageId;

      addLocalMessage({
        id: tempUserMessageId,
        session_id: activeSessionId!,
        message_type: 'user',
        content_text: messageText,
        page_number: pageNumber,
        attachments: messageAttachments,
        created_at: new Date().toISOString()
      });

      // Show thinking animation
      setIsThinking(true);

      // Call Agent-based chat API with SSE streaming and tool status updates
      // Use saved data_urls (before S3 upload cleared them)
      const stream = await streamAgentChat(bookIdStr, {
        query: messageText,
        current_page: pageNumber,
        session_id: activeSessionId!,
        images: attachmentsWithDataUrls.filter(att => att.data_url)  // Only send if data_url exists
      });

      // Stream AI response with status updates
      const reader = stream.getReader();
      const decoder = new TextDecoder();
      aiMessageId = `temp-ai-${Date.now()}`;
      let firstTokenReceived = false;
      let currentEventType = '';

      // Read stream and update message in real-time
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split('\n');

        for (const line of lines) {
          // Parse SSE event type
          if (line.startsWith('event: ')) {
            currentEventType = line.slice(7).trim();
            continue;
          }
          
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));

              // Handle different event types
              if (currentEventType === 'status') {
                // Show tool progress message (🧠 Analyzing... / 📚 Searching... / ✨ Synthesizing...)
                // Only show status if user is still viewing the streaming session
                if (activeSessionIdRef.current === currentStreamingSessionId) {
                  setAgentStatus(data.message);
                }
                console.log('Agent status:', data.message);
              }
              else if (currentEventType === 'content') {
                // Hide thinking on first content token
                // Don't clear agentStatus here - it's already hidden once content appears,
                // and clearing it causes flickering. Status is cleared in done/finally.
                if (!firstTokenReceived) {
                  if (activeSessionIdRef.current === currentStreamingSessionId) {
                    setIsThinking(false);
                  }
                  firstTokenReceived = true;
                }

                markdownContent += data.text;

                // Check if user is still viewing the streaming session
                if (activeSessionIdRef.current === currentStreamingSessionId) {
                  // Update visible messages
                  setMessages(prev =>
                    prev.map(msg =>
                      msg.id === aiMessageId
                        ? { ...msg, content_text: markdownContent }
                        : msg
                    )
                  );
                } else {
                  // Update cache for background streaming
                  updateCacheForSession(currentStreamingSessionId!, prev =>
                    prev.map(msg =>
                      msg.id === aiMessageId
                        ? { ...msg, content_text: markdownContent }
                        : msg
                    )
                  );
                }
              }
              else if (currentEventType === 'user_message') {
                // Backend created user message - update temp ID with real ID
                if (data.id) {
                  console.log(`User message created by backend with ID: ${data.id}`);
                  // Check if user is still viewing the streaming session
                  if (activeSessionIdRef.current === currentStreamingSessionId) {
                    setMessages(prev =>
                      prev.map(msg =>
                        msg.id === userMessageId ? { ...msg, id: data.id } : msg
                      )
                    );
                  } else {
                    // Update cache for background streaming
                    updateCacheForSession(currentStreamingSessionId!, prev =>
                      prev.map(msg =>
                        msg.id === userMessageId ? { ...msg, id: data.id } : msg
                      )
                    );
                  }
                  userMessageId = data.id; // Update the reference for AI message linking
                }
              }
              else if (currentEventType === 'ai_message') {
                // Backend created AI message placeholder - use this ID (UUID string)
                if (data.id) {
                  aiMessageId = data.id;
                  console.log(`AI message created by backend with ID: ${data.id}`);

                  const aiPlaceholderMessage = {
                    id: data.id,
                    session_id: currentStreamingSessionId!,
                    message_type: 'ai' as const,
                    content_text: '',
                    created_at: new Date().toISOString()
                  };

                  // Check if user is still viewing the streaming session
                  if (activeSessionIdRef.current === currentStreamingSessionId) {
                    // Add to visible messages
                    addLocalMessage(aiPlaceholderMessage);
                  } else {
                    // Add to cache for background streaming
                    addMessageToCache(currentStreamingSessionId!, aiPlaceholderMessage);
                  }
                  setStreamingMessageId(data.id);
                }
              }
              else if (currentEventType === 'title') {
                // Session title generated - update in state
                if (data.title && data.session_id) {
                  updateSessionTitle(data.session_id, data.title);
                  console.log(`Session title updated: ${data.title}`);
                }
              }
              else if (currentEventType === 'done') {
                // Streaming complete - clear status only if user is viewing this session
                if (activeSessionIdRef.current === currentStreamingSessionId) {
                  setAgentStatus('');
                }
                console.log('Streaming complete');

                // Refresh AI message from DB as safety net (ensures complete message)
                if (aiMessageId && !aiMessageId.startsWith('temp-')) {
                  try {
                    const response = await api.get(`/api/chat/messages/${aiMessageId}`);
                    const dbMessage = response.data;

                    if (activeSessionIdRef.current === currentStreamingSessionId) {
                      // Update visible messages with DB content
                      setMessages(prev => prev.map(msg =>
                        msg.id === aiMessageId
                          ? { ...msg, content_text: dbMessage.content_text }
                          : msg
                      ));
                    } else {
                      // Update cache with DB content
                      updateCacheForSession(currentStreamingSessionId!, prev => prev.map(msg =>
                        msg.id === aiMessageId
                          ? { ...msg, content_text: dbMessage.content_text }
                          : msg
                      ));
                    }
                    console.log('[Dhyana] Refreshed AI message from DB');
                  } catch (err) {
                    console.error('Failed to refresh message from DB:', err);
                    // Non-critical - streamed content is still there
                  }
                }
              }
              else if (currentEventType === 'error') {
                // Only clear status/thinking if user is viewing this session
                if (activeSessionIdRef.current === currentStreamingSessionId) {
                  setAgentStatus('');
                  setIsThinking(false); // Clear thinking indicator on error
                }

                // Update AI message with error text
                if (aiMessageId) {
                  const errorMessage = data.message || 'An error occurred while processing your question. Please try again.';
                  // Check if user is still viewing the streaming session
                  if (activeSessionIdRef.current === currentStreamingSessionId) {
                    setMessages(prev => prev.map(msg =>
                      msg.id === aiMessageId
                        ? { ...msg, content_text: errorMessage }
                        : msg
                    ));
                  } else {
                    // Update cache for background streaming
                    updateCacheForSession(currentStreamingSessionId!, prev => prev.map(msg =>
                      msg.id === aiMessageId
                        ? { ...msg, content_text: errorMessage }
                        : msg
                    ));
                  }
                }
                // No toast needed - error is visible in chat bubble
              }
            } catch (e) {
              // Ignore parse errors
              console.error('Error parsing SSE data:', e);
            }
          }
        }
      }

      // Streaming complete - backend has already persisted the AI response
      // No need to save it again from frontend
      console.log('Streaming complete, AI message already persisted by backend');

      // Streaming complete - clear streaming state
      setStreamingMessageId(null);
      setStreamingSessionId(null);
      isRequestInFlight.current = false;

      // Auto-scroll to bottom after streaming completes
      if (chatAreaRef.current) {
        chatAreaRef.current.scrollTop = chatAreaRef.current.scrollHeight;
      }

    } catch (error) {
      console.error('Failed to get AI response:', error);
      setIsThinking(false);
      setStreamingMessageId(null);
      setStreamingSessionId(null);
      isRequestInFlight.current = false;

      // Check if user is still viewing the streaming session
      const isViewingStreamingSession = activeSessionIdRef.current === currentStreamingSessionId;

      // Update AI message to show error state if we got partial content
      if (markdownContent && aiMessageId) {
        const errorContent = markdownContent + '\n\n_[Response interrupted due to error]_';
        if (isViewingStreamingSession) {
          setMessages(prev => prev.map(msg =>
            msg.id === aiMessageId
              ? { ...msg, content_text: errorContent }
              : msg
          ));
        } else {
          updateCacheForSession(currentStreamingSessionId!, prev => prev.map(msg =>
            msg.id === aiMessageId
              ? { ...msg, content_text: errorContent }
              : msg
          ));
        }
      }

      // Remove AI message from UI if we got no content
      if (!markdownContent && aiMessageId) {
        if (isViewingStreamingSession) {
          setMessages(prev => prev.filter(msg => msg.id !== aiMessageId));
        } else {
          updateCacheForSession(currentStreamingSessionId!, prev => prev.filter(msg => msg.id !== aiMessageId));
        }
      }

      // Only restore input and show error message if user is still on this session
      if (isViewingStreamingSession) {
        // Restore input on error
        setChatInput(messageText);
        setAttachments(messageAttachments);

        // Show error message
        addLocalMessage({
          id: `temp-error-${Date.now()}`,
          session_id: currentStreamingSessionId!,
          message_type: 'system',
          content_text: 'Failed to get response from AI. Please try again.',
          created_at: new Date().toISOString()
        });
      }
    }
  };

  const createNewSession = async () => {
    try {
      const sessionName = `Chat ${chatSessions.length + 1}`;
      await createSession({
        book_id: bookIdStr,
        session_name: sessionName,
        is_active: true,
        last_page_number: pageNumber,
        ...(courseId && courseItemId
          ? { session_metadata: { source: 'course', course_id: courseId, course_item_id: courseItemId } }
          : {})
      });
      
      // Messages will be automatically cleared by useChatMessages hook
      // when activeSessionId changes
      
      setShowSessionMenu(false);
    } catch (error) {
      console.error('Failed to create new session:', error);
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    Array.from(files).forEach(file => {
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (event) => {
          const dataUrl = event.target?.result as string;
          const newAttachment: MessageAttachment = {
            type: 'image',
            data_url: dataUrl,
            file_name: file.name,
            mime_type: file.type,
            page_number: pageNumber, // Include current page for context
            metadata: {
              file_size: file.size
            }
          };
          setAttachments(prev => [...prev, newAttachment]);
        };
        reader.readAsDataURL(file);
      }
    });

    // Reset input
    e.target.value = '';
  };

  const handleThoughtpadFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    Array.from(files).forEach(file => {
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (event) => {
          const dataUrl = event.target?.result as string;
          const newAttachment: MessageAttachment = {
            type: 'image',
            data_url: dataUrl,
            file_name: file.name,
            mime_type: file.type,
            page_number: pageNumber,
            metadata: {
              file_size: file.size
            }
          };
          setThoughtpadAttachments(prev => [...prev, newAttachment]);
        };
        reader.readAsDataURL(file);
      }
    });

    // Reset input
    e.target.value = '';
  };



  const handleTextSelection = (e: React.MouseEvent) => {
    // Don't prevent default or stop propagation - let text selection work naturally
    
    // Increase delay to ensure selection is complete
    setTimeout(() => {
    const selection = window.getSelection();
      // Don't trim the text to preserve formatting and full selection
      const selectedText = selection?.toString();
      
    if (selectedText && selectedText.length > 0) {
        // If in multi-page selection mode, add to selections
        if (isMultiPageSelecting) {
          // Add current selection to multi-page selections
          setMultiPageSelections(prev => {
            // Check if we already have a selection for this page
            const existingIndex = prev.findIndex(sel => sel.pageNumber === pageNumber);
            if (existingIndex >= 0) {
              // Replace existing selection for this page
              const updated = [...prev];
              updated[existingIndex] = { pageNumber, text: selectedText };
              return updated;
            } else {
              // Add new selection
              return [...prev, { pageNumber, text: selectedText }].sort((a, b) => a.pageNumber - b.pageNumber);
            }
          });
          
          // Clear the selection
          window.getSelection()?.removeAllRanges();
          return;
        }
        
        // Directly add to chat or thoughtpad based on active tab
        if (activeTab === 'chat') {
          // Add to chat input with "Pls explain this:" prefix
          const newText = `Pls explain this: "${selectedText}"`;
          setChatInput(prev => prev ? `${prev}\n\n${newText}` : newText);
        } else {
          // Add to thoughtpad input
          setThoughtpadInput(prev => prev ? `${prev}\n\n${selectedText}` : selectedText);
        }
        
        // Clear the selection
        window.getSelection()?.removeAllRanges();
      }
    }, 150); // Increased delay to ensure full text selection is captured
  };

  const addToThoughtpad = async (
    text: string,
    noteType: NoteType = 'thought',
    imageUrls: string[] = [],
    conceptTags: string[] = [],
    metadata: Record<string, any> = {}
  ) => {
    // Close modal and reset state IMMEDIATELY (before async operation)
    setShowAddNoteModal(false);
    setNoteText('');
    setNoteAttachments([]);

    try {
      // Extract chat linkage from metadata for chat_answer notes
      const chatMessageId = metadata.chat_message_id || undefined;

      // Remove chat_message_id from metadata since it goes to dedicated field
      const { chat_message_id, ...cleanMetadata } = metadata;

      // For thoughts, text goes to user_note. For highlights, it goes to content_text
      const noteData = {
        book_id: bookIdStr,
        page_number: pageNumber,
        note_type: noteType,
        content_text: noteType === 'thought' ? '' : text, // Only for highlights
        user_note: noteType === 'thought' ? text : (cleanMetadata.user_note || undefined), // For thoughts or user comments
        image_urls: imageUrls,
        concept_tags: conceptTags,
        note_metadata: cleanMetadata,
        chat_message_id: chatMessageId  // Backend will derive source_question_id from this
      };

      await createNote(noteData);

      // Refresh notes for current page
      await fetchNotesForPage(pageNumber);

      setActiveTab('thoughtpad');
    } catch (error) {
      console.error('Failed to add note:', error);
      showToast('error', 'Failed to create note', 'Please try again');
    }
  };

  const toggleNoteExpanded = (note: ReadingNote) => {
    setExpandedNotes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(note.id)) {
        newSet.delete(note.id);
      } else {
        newSet.add(note.id);
      }
      return newSet;
    });
  };



  // Navigate to single note view when clicking "View Note" links
  const scrollToNote = useCallback((noteId: string) => {
    // Store current page for return
    sessionStorage.setItem('returnToPage', JSON.stringify({
      bookId: bookIdStr,
      pageNumber: pageNumber,
    }));
    // Navigate to dedicated single note view
    navigate(`/library/${bookIdStr}/note/${noteId}`);
  }, [bookIdStr, pageNumber, navigate]);

  // Handle page link navigation from AI messages
  const handlePageLinkClick = useCallback((linkedBookId: string, targetPage: number) => {
    // Only handle links for the current book
    if (linkedBookId !== bookIdStr) {
      showToast('info', 'Different Book', 'This link references a different book');
      return;
    }
    
    // Clear any existing timeout from previous link clicks
    if (returnButtonTimeoutRef.current) {
      clearTimeout(returnButtonTimeoutRef.current);
      returnButtonTimeoutRef.current = null;
    }

    // Store current page for return
    setPreviousPage(pageNumber);
    setShowReturnButton(true);

    // Navigate to the target page
    setPageNumber(targetPage);
  }, [bookIdStr, pageNumber, showToast]);

  // Handle returning to previous page
  const handleReturnToPreviousPage = useCallback(() => {
    if (previousPage !== null) {
      setPageNumber(previousPage);
      setPreviousPage(null);
      setShowReturnButton(false);
      
      // Clear the timeout
      if (returnButtonTimeoutRef.current) {
        clearTimeout(returnButtonTimeoutRef.current);
        returnButtonTimeoutRef.current = null;
      }
    }
  }, [previousPage]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (returnButtonTimeoutRef.current) {
        clearTimeout(returnButtonTimeoutRef.current);
      }
    };
  }, []);

  // Handle returning to chat
  const handleReturnToChat = useCallback(() => {
    if (returnToChat) {
      sessionStorage.removeItem('returnToChat');
      if (returnToChat.sessionId) {
        navigate(`/chat?session=${returnToChat.sessionId}`);
      } else {
        navigate('/chat');
      }
    }
  }, [returnToChat, navigate]);

  const handleImageClick = (e: React.MouseEvent, note: ReadingNote, imageIndex: number = 0) => {
    e.stopPropagation();
    setViewingNote(note);
    setCurrentImageIndex(imageIndex);
    setShowNoteViewerModal(true);
  };
  
  const handleMessageAttachmentClick = (e: React.MouseEvent, message: ChatMessage, attachmentIndex: number = 0) => {
    e.stopPropagation();
    setViewingMessage(message);
    setCurrentMessageImageIndex(attachmentIndex);
    setShowMessageViewerModal(true);
  };
  
  const truncateText = (text: string, maxLength: number = 150): { truncated: string; isTruncated: boolean } => {
    // Remove HTML tags for character counting
    const plainText = text.replace(/<[^>]*>/g, '');
    
    if (plainText.length <= maxLength) {
      return { truncated: text, isTruncated: false };
    }
    
    // Find a good breaking point (end of word)
    let breakPoint = maxLength;
    while (breakPoint > 0 && plainText[breakPoint] !== ' ') {
      breakPoint--;
    }
    
    if (breakPoint === 0) breakPoint = maxLength;
    
    // Extract the truncated HTML-aware text
    let charCount = 0;
    let htmlIndex = 0;
    let inTag = false;
    
    while (htmlIndex < text.length && charCount < breakPoint) {
      if (text[htmlIndex] === '<') {
        inTag = true;
      } else if (text[htmlIndex] === '>') {
        inTag = false;
      } else if (!inTag) {
        charCount++;
      }
      htmlIndex++;
    }
    
    return {
      truncated: text.substring(0, htmlIndex) + '...',
      isTruncated: true
    };
  };
  
  const toggleTextExpansion = (messageId: string) => {
    setExpandedTextMessages(prev => {
      const newSet = new Set(prev);
      if (newSet.has(messageId)) {
        newSet.delete(messageId);
      } else {
        newSet.add(messageId);
      }
      return newSet;
    });
  };

  // Snipping tool functions
  const handleStartSnipping = () => {
    setIsSnipping(true);
    setSnipStart(null);
    setSnipEnd(null);
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!isSnipping) return;
    
    const pageElement = document.querySelector('.react-pdf__Page') as HTMLElement;
    if (!pageElement) return;
    
    const rect = pageElement.getBoundingClientRect();
    setSnipStart({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isSnipping || !snipStart) return;
    
    const pageElement = document.querySelector('.react-pdf__Page') as HTMLElement;
    if (!pageElement) return;
    
    const rect = pageElement.getBoundingClientRect();
    setSnipEnd({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });
  };

  const handleMouseUp = async () => {
    if (!isSnipping || !snipStart || !snipEnd) return;
    
    // Don't capture if selection is too small
    const width = Math.abs(snipEnd.x - snipStart.x);
    const height = Math.abs(snipEnd.y - snipStart.y);
    if (width < 10 || height < 10) {
      setIsSnipping(false);
      setSnipStart(null);
      setSnipEnd(null);
      return;
    }
    
    try {
      // Get the PDF canvas
      const pageCanvas = document.querySelector('.react-pdf__Page__canvas') as HTMLCanvasElement;
      if (!pageCanvas) {
        console.error('PDF canvas not found');
        return;
      }
      
      // Get the container dimensions for proper scaling
      const container = pdfContentRef.current;
      if (!container) return;
      
      const canvasRect = pageCanvas.getBoundingClientRect();
      
      // Calculate scale factors
      const scaleX = pageCanvas.width / canvasRect.width;
      const scaleY = pageCanvas.height / canvasRect.height;
      
      // Calculate selection bounds relative to canvas
      const x = Math.min(snipStart.x, snipEnd.x) * scaleX;
      const y = Math.min(snipStart.y, snipEnd.y) * scaleY;
      const scaledWidth = width * scaleX;
      const scaledHeight = height * scaleY;
      
      // Create output canvas
      const outputCanvas = document.createElement('canvas');
      outputCanvas.width = scaledWidth;
      outputCanvas.height = scaledHeight;
      
      const ctx = outputCanvas.getContext('2d');
      if (!ctx) return;
      
      // Draw the selected area
      ctx.drawImage(
        pageCanvas,
        x, y, scaledWidth, scaledHeight,
        0, 0, scaledWidth, scaledHeight
      );
      
      // Convert to data URL
      const dataUrl = outputCanvas.toDataURL('image/png');
      
      // Show context menu for snippet
      // Position menu smartly based on available space
      const selectionRight = Math.max(snipStart.x, snipEnd.x);
      const selectionBottom = Math.max(snipStart.y, snipEnd.y);
      const selectionLeft = Math.min(snipStart.x, snipEnd.x);
      const selectionTop = Math.min(snipStart.y, snipEnd.y);
      
      // Initial position at bottom-right of selection
      let menuX = selectionRight + 10;
      let menuY = selectionBottom + 10;
      
      // Check if menu would go off edges and adjust
      const pdfContainer = pdfContentRef.current;
      if (pdfContainer) {
        const menuWidth = 350; // Approximate menu width
        const menuHeight = 300; // Approximate menu height
        
        // Check right edge
        if (menuX + menuWidth > pdfContainer.clientWidth - 20) {
          // Position to the left of selection instead
          menuX = selectionLeft - menuWidth - 10;
        }
        
        // Check bottom edge
        if (menuY + menuHeight > pdfContainer.clientHeight - 20) {
          // Position above selection instead
          menuY = selectionTop - menuHeight - 10;
        }
        
        // Ensure menu doesn't go off left or top
        menuX = Math.max(20, menuX);
        menuY = Math.max(20, menuY);
      }
      
      // Calculate bounds for snippet relative to PDF page
      const pageElement = document.querySelector('.react-pdf__Page') as HTMLElement;
      let bounds = {
        x: selectionLeft,
        y: selectionTop,
        width: selectionRight - selectionLeft,
        height: selectionBottom - selectionTop
      };
      
      if (pageElement) {
        const pageRect = pageElement.getBoundingClientRect();
        const containerRect = pdfContentRef.current!.getBoundingClientRect();
        // Adjust for the page position within the container
        bounds = {
          x: (selectionLeft + containerRect.left - pageRect.left) / (zoomLevel / 100),
          y: (selectionTop + containerRect.top - pageRect.top) / (zoomLevel / 100),
          width: (selectionRight - selectionLeft) / (zoomLevel / 100),
          height: (selectionBottom - selectionTop) / (zoomLevel / 100)
        };
      }
      
      // Create attachment object
      const newAttachment: MessageAttachment = {
        type: 'snippet',
        data_url: dataUrl,
        page_number: pageNumber,
        metadata: {
          ...bounds,
          selection_type: 'snippet',
          captured_at: new Date().toISOString()
        }
      };
      
      // Directly add to chat or thoughtpad based on active tab
      if (activeTab === 'chat') {
        setAttachments(prev => [...prev, newAttachment]);
      } else {
        setThoughtpadAttachments(prev => [...prev, newAttachment]);
      }
    } catch (error) {
      console.error('Error capturing PDF area:', error);
    } finally {
      setIsSnipping(false);
      setSnipStart(null);
      setSnipEnd(null);
    }
  };




  // Functions for handling annotations - DISABLED FOR NOW
  // const saveAnnotations = useCallback((annotations: Annotation[]) => {
  //   localStorage.setItem(`annotations_${bookId}`, JSON.stringify(annotations));
  // }, [bookId]);

  // const loadAnnotations = useCallback((): Annotation[] => {
  //   const saved = localStorage.getItem(`annotations_${bookId}`);
  //   if (saved) {
  //     try {
  //       return JSON.parse(saved) as Annotation[];
  //     } catch (error) {
  //       console.error('Error loading annotations:', error);
  //       return [];
  //     }
  //   }
  //   return [];
  // }, [bookId]);

  // Load annotations for current page - DISABLED FOR NOW
  // useEffect(() => {
  //   if (pageNumber && bookId) {
  //     const allAnnotations = loadAnnotations();
  //     const currentPageAnnotations = allAnnotations.filter(
  //       annotation => annotation.pageNumber === pageNumber
  //     );
  //     setPageAnnotations(currentPageAnnotations);
  //   }
  // }, [pageNumber, bookId, loadAnnotations]);

  // Force re-render annotations on window resize to maintain positions - DISABLED FOR NOW
  // useEffect(() => {
  //   const handleResize = () => {
  //     // Force a small delay to let PDF re-render first
  //     setTimeout(() => {
  //       setPageAnnotations(prev => [...prev]);
  //     }, 100);
  //   };

  //   window.addEventListener('resize', handleResize);
  //   return () => window.removeEventListener('resize', handleResize);
  // }, []);

  // Function to submit thoughtpad input as a note
  const handleSubmitThoughtpadNote = async () => {
    if (!thoughtpadInput.trim() && thoughtpadAttachments.length === 0) return;
    
    const imageUrls = thoughtpadAttachments
      .filter(a => a.type === 'snippet' && a.data_url)
      .map(a => a.data_url as string);
    
    // Add to thoughtpad
    await addToThoughtpad(
      thoughtpadInput,
      'thought',
      imageUrls,
      [],
      { zoom_level: zoomLevel }
    );
    
    // Clear input
    setThoughtpadInput('');
    setThoughtpadAttachments([]);
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    // Show keyboard shortcuts help with Cmd/Ctrl + / (or Cmd/Ctrl + ?)
    // Accept both / and ? since ? is Shift+/ and browsers report differently
    if ((e.key === '/' || e.key === '?') && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      setShowKeyboardHelp(true);
      return;
    }

    // Check for Cmd/Ctrl + B to toggle sidebar
    if ((e.metaKey || e.ctrlKey) && e.key === 'b') {
      e.preventDefault();
      setIsSidebarVisible(prev => !prev);
      setPdfKey(prev => prev + 1); // Force PDF re-render
    } else if ((e.metaKey || e.ctrlKey) && e.key === 't') {
      // Toggle TL;DR
      e.preventDefault();
      setShowSummary(prev => !prev);
      if (!showSummary) {
        fetchTLDR();
      }
    } else if ((e.metaKey || e.ctrlKey) && e.key === '=' || (e.metaKey || e.ctrlKey) && e.key === '+') {
      // Zoom in
      e.preventDefault();
      setZoomLevel(prev => Math.min(200, prev + 10));
    } else if ((e.metaKey || e.ctrlKey) && e.key === '-') {
      // Zoom out
      e.preventDefault();
      setZoomLevel(prev => Math.max(50, prev - 10));
    } else if ((e.metaKey || e.ctrlKey) && e.key === '0') {
      // Reset zoom
      e.preventDefault();
      setZoomLevel(100);
    } else if ((e.metaKey || e.ctrlKey) && e.key === 'n' && activeTab === 'thoughtpad') {
      // Add Note shortcut
      e.preventDefault();
      setShowAddNoteModal(true);
    } else if (e.key === 'Escape') {
      if (showKeyboardHelp) {
        setShowKeyboardHelp(false);
      } else if (showSummary) {
        setShowSummary(false);
      } else if (isSnipping) {
        setIsSnipping(false);
        setSnipStart(null);
        setSnipEnd(null);
      } else if (showAddNoteModal) {
        setShowAddNoteModal(false);
        setNoteText('');
        setNoteAttachments([]);
      } else if (isMultiPageSelecting) {
        setIsMultiPageSelecting(false);
        setMultiPageSelections([]);
      }
    } else if ((e.metaKey || e.ctrlKey) && e.key === 'm') {
      // Toggle multi-page selection mode
      e.preventDefault();
      if (isMultiPageSelecting) {
        // Finish selection
        if (multiPageSelections.length > 0) {
          const combinedText = multiPageSelections
            .map(sel => `[Page ${sel.pageNumber}]\n${sel.text}`)
            .join('\n\n');
          
          // Reset multi-page state first
          setIsMultiPageSelecting(false);
          setMultiPageSelections([]);
          
          // Directly add to chat or thoughtpad based on active tab
          if (activeTab === 'chat') {
            const newText = `Pls explain this:\n\n${combinedText}`;
            setChatInput(prev => prev ? `${prev}\n\n${newText}` : newText);
          } else {
            setThoughtpadInput(prev => prev ? `${prev}\n\n${combinedText}` : combinedText);
          }
        } else {
          setIsMultiPageSelecting(false);
          setMultiPageSelections([]);
        }
      } else {
        setIsMultiPageSelecting(true);
        setMultiPageSelections([]);
      }
    } else if (e.key === 'ArrowLeft' && pageNumber > 1 && !isSnipping) {
      // Don't navigate pages if user is typing in an input field
      const activeElement = document.activeElement;
      const isInputFocused = activeElement instanceof HTMLInputElement ||
                            activeElement instanceof HTMLTextAreaElement ||
                            activeElement?.getAttribute('contenteditable') === 'true';
      if (!isInputFocused) {
        setPageNumber(prev => prev - 1);
      }
    } else if (e.key === 'ArrowRight' && pageNumber < numPages && !isSnipping) {
      // Don't navigate pages if user is typing in an input field
      const activeElement = document.activeElement;
      const isInputFocused = activeElement instanceof HTMLInputElement ||
                            activeElement instanceof HTMLTextAreaElement ||
                            activeElement?.getAttribute('contenteditable') === 'true';
      if (!isInputFocused) {
        setPageNumber(prev => prev + 1);
      }
    }
  };

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [pageNumber, numPages, isSnipping, isSidebarVisible, zoomLevel, activeTab]);

  // Trigger resize when sidebar visibility changes
  useEffect(() => {
    // Small delay to ensure DOM has updated
    const timer = setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    }, 50);
    return () => clearTimeout(timer);
  }, [isSidebarVisible]);

  // MOBILE/TABLET BLOCK - Check before loading state to show modal immediately
  if (isMobile || isTablet) {
    return (
      <div className={`min-h-screen ${isDarkMode ? 'dark bg-[#030712]' : 'light-theme bg-[#FAFBFC]'}`}>
        <MobileReaderModal
          isOpen={true}
          onClose={() => navigate('/library', { replace: true })}
          bookTitle={book?.title}
        />
      </div>
    );
  }

  if (loading) {
    // Calculate message index based on download progress percentage
    const getMessageIndex = () => {
      if (!loadProgress || loadProgress.total === 0) {
        return 0; // Default to first message when no progress data
      }
      const pct = loadProgress.percentage;
      if (pct >= 75) return 3;
      if (pct >= 50) return 2;
      if (pct >= 25) return 1;
      return 0;
    };
    const currentMessage = loadingMessages[getMessageIndex()];

    // Format bytes to human readable string
    const formatBytes = (bytes: number): string => {
      if (bytes === 0) return '0 B';
      const k = 1024;
      const sizes = ['B', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
    };

    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFBFC] dark:bg-[#030712]">
        <div className="text-center max-w-md px-6">
          <ArrowPathIcon className="h-10 w-10 text-cyan-500 animate-spin mx-auto mb-4" />
          <p className="text-lg font-medium text-slate-900 dark:text-white mb-2">
            {currentMessage.message}
          </p>
          {currentMessage.subMessage && (
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-3">
              {currentMessage.subMessage}
            </p>
          )}

          {/* Progress indicator */}
          {loadProgress && loadProgress.total > 0 && (
            <div className="mt-4">
              {/* Progress bar */}
              <div className="w-full h-2 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-cyan-500 to-blue-500 transition-all duration-300"
                  style={{ width: `${loadProgress.percentage}%` }}
                />
              </div>
              {/* Progress text */}
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-2">
                {formatBytes(loadProgress.loaded)} / {formatBytes(loadProgress.total)} ({loadProgress.percentage}%)
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (error && !pdfUrl) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFBFC] dark:bg-[#030712]">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={() => navigate('/library')}
            className="text-[#64748B] dark:text-[#06B6D4] hover:underline"
          >
            Back to Library
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`h-screen overflow-hidden ${isDarkMode ? 'dark bg-[#030712]' : 'light-theme bg-[#FAFBFC]'}`}>
      {/* Course "up next" handoff — slides up when the student finishes a course reading */}
      {courseUpNext && (() => {
        const next = courseUpNext.next;
        const isReading = next?.item_type === 'reading' && !!next?.book_id;
        const goNext = () => {
          if (isReading && next) {
            navigate(
              `/dhyana/${next.book_id}?page=${next.page_start ?? 1}` +
                `&end=${next.page_end ?? ''}&course=${courseId}&item=${next.id}`
            );
          } else {
            navigate(`/my-courses/${courseId}`);
          }
        };
        return (
          <>
            {/* Full handoff card — shown on completion and when the pill is tapped.
                The container is always pointer-events-none so its transparent
                full-width strip never swallows clicks meant for the chat send
                button etc.; only the card itself (below) is interactive. */}
            <div
              className={`pointer-events-none fixed inset-x-0 bottom-6 z-[70] flex justify-center px-4 transition-all duration-500 ease-out ${
                upNextOpen ? 'translate-y-0 opacity-100' : 'translate-y-28 opacity-0'
              }`}
            >
              <div className={`pointer-events-auto flex w-full max-w-md items-center gap-3 rounded-2xl border p-3.5 shadow-2xl backdrop-blur ${
                isDarkMode ? 'border-cyan-500/30 bg-slate-900/90' : 'border-cyan-500/30 bg-white/95'
              }`}>
                <span className="flex h-10 w-10 flex-none items-center justify-center rounded-full bg-cyan-500/15 text-cyan-500">
                  <CheckCircleIcon className="h-6 w-6" />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-cyan-600 dark:text-cyan-400">
                    Reading complete{next ? ` · Up next` : ''}
                  </div>
                  <p className={`truncate text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {next
                      ? (isReading ? (next.book_title || 'Next reading') : (next.assignment_title || 'Next: assignment'))
                      : "You've finished this course 🎉"}
                  </p>
                </div>
                <button
                  onClick={goNext}
                  className="inline-flex flex-none items-center gap-1.5 rounded-xl bg-cyan-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-cyan-600"
                >
                  {next ? (isReading ? 'Continue' : 'Go to course') : 'Back to course'}
                  <ArrowRightIcon className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setUpNextOpen(false)}
                  aria-label="Dismiss"
                  className={`flex-none rounded-lg p-1.5 transition ${isDarkMode ? 'text-slate-400 hover:bg-white/10' : 'text-gray-400 hover:bg-gray-100'}`}
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* Persistent nudge — once dismissed, while there are still items left
                in the course (even if they keep reading past the end page) a small
                pill stays in the corner to carry them forward. Tapping re-opens the
                card. The last item has nothing next, so it dismisses for good. */}
            {next && (
              <button
                onClick={() => setUpNextOpen(true)}
                className={`fixed bottom-6 left-1/2 z-[60] inline-flex -translate-x-1/2 items-center gap-2 rounded-full border px-4 py-2.5 text-sm font-semibold shadow-lg backdrop-blur transition-all duration-300 ${
                  upNextOpen ? 'pointer-events-none translate-y-4 opacity-0' : 'translate-y-0 opacity-100'
                } ${isDarkMode ? 'border-cyan-500/30 bg-slate-900/90 text-cyan-300 hover:bg-slate-800' : 'border-cyan-500/30 bg-white/95 text-cyan-700 hover:bg-cyan-50'}`}
              >
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-cyan-500/15">
                  <ArrowRightIcon className="h-3.5 w-3.5" />
                </span>
                Up next
              </button>
            )}
          </>
        );
      })()}
      <main className="w-full h-full flex flex-row p-4">
        
        {/* Left Pane - PDF Viewer */}
        <div id="viewer-container" className={`flex-1 min-w-0 h-full flex flex-col glass-pane overflow-hidden relative ${isSidebarVisible ? 'mr-4' : ''}`}>
          {/* Header */}
          <div className="flex-shrink-0 p-3 bg-transparent border-b border-white/10 dark:border-white/10 flex justify-between items-center z-10">
            {/* Breadcrumb Navigation */}
            <div className="flex items-center gap-2 text-sm min-w-0 flex-1">
              <button
                onClick={() => navigate('/library')}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors flex items-center gap-1"
              >
                <ArrowLeftIcon className="h-3.5 w-3.5" />
                <span>Library</span>
              </button>
              <ChevronRightIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
              <button
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors truncate max-w-[300px]"
                title={book?.title}
              >
                {book?.title || 'Loading...'}
              </button>
              <ChevronRightIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
              <span className="text-gray-900 dark:text-white font-medium flex items-center gap-2">
                <span>Page {pageNumber}</span>
                <span className="text-xs text-gray-400">
                  ({numPages ? Math.round((pageNumber / numPages) * 100) : 0}%)
                </span>
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button
                id="tour-help"
                onClick={() => {
                  setShowHelpHighlight(false);
                  setShowTourTooltip(false);
                  startTour();
                }}
                className={`pro-button p-2 rounded-lg ${showHelpHighlight ? 'animate-pulse ring-2 ring-cyan-400 ring-offset-2 ring-offset-transparent' : ''}`}
                data-tooltip-id="tour-help-tooltip"
                data-tooltip-content="Take a quick tour"
              >
                <QuestionMarkCircleIcon className={`h-4 w-4 ${showHelpHighlight ? 'text-cyan-400' : ''}`} />
              </button>
              <button
                onClick={toggleDarkMode}
                className="pro-button p-2 rounded-lg"
                data-tooltip-id="dhyana-tooltip"
                data-tooltip-content={isDarkMode ? "Switch to light mode" : "Switch to dark mode"}
              >
                {isDarkMode ? <SunIcon className="h-4 w-4" /> : <MoonIcon className="h-4 w-4" />}
              </button>
              {/* Mnemo — on-demand comprehension check */}
              <button
                id="tour-mnemo"
                onClick={() => {
                  if (!isPulseActive && !streamingMessageId && !isThinking) {
                    setManualPulseCheck(true);
                  }
                }}
                disabled={isPulseActive}
                className={`px-3 py-2 text-sm font-semibold rounded-lg flex items-center gap-1.5 transition-all shadow-md hover:shadow-lg ${
                  isPulseActive
                    ? 'bg-amber-500/50 text-white/70 cursor-not-allowed'
                    : 'bg-amber-500 hover:bg-amber-600 text-white'
                }`}
                data-tooltip-id="dhyana-tooltip"
                data-tooltip-content="Mnemo — test your understanding"
              >
                <BoltIcon className="h-3.5 w-3.5" />
              </button>
              <button
                id="tour-tldr"
                onClick={() => {
                  if (!showSummary) {
                    fetchTLDR();
                  }
                  setShowSummary(!showSummary);
                }}
                className="bg-[#06B6D4] hover:bg-[#0891B2] px-4 py-2 text-sm font-semibold text-white rounded-lg flex items-center gap-2 transition-all shadow-md hover:shadow-lg"
                data-tooltip-id="dhyana-tooltip"
                data-tooltip-content="Generate a quick summary of the current page"
              >
                TL;DR
              </button>
            </div>
          </div>

          {/* PDF Content */}
          <div 
            id="pdf-content"
            ref={pdfContentRef}
            className={`flex-grow overflow-hidden relative transition-all duration-300 ${showSummary ? 'brightness-75' : ''} flex`}
          >
            {/* Outline Sidebar */}
            {showOutline && resolvedOutline.length > 0 && (
              <>
                <div 
                  className="glass-pane h-full overflow-hidden flex flex-col"
                  style={{ width: `${outlineWidth}px` }}
                >
                  <div className="px-4 py-3 border-b border-white/10 dark:border-white/10 flex items-center justify-between">
                    <h3 className="text-sm font-bold uppercase tracking-wider text-[#64748B] dark:text-[#06B6D4]">Contents</h3>
                    <button
                      onClick={() => setShowOutline(false)}
                      className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                    >
                      <XMarkIcon className="h-4 w-4 text-gray-500" />
                    </button>
                  </div>
                  <div className="flex-1 overflow-y-auto py-2 px-2">
                    {resolvedOutline.map((item, index) => (
                      <OutlineItem
                        key={index}
                        item={item}
                        level={0}
                        siblings={resolvedOutline}
                        itemIndex={index}
                        upperBound={numPages + 1}
                      />
                    ))}
                  </div>
                </div>
                
                {/* Resize Handle */}
                <div
                  className="w-1 cursor-col-resize hover:bg-[#64748B]/30 dark:hover:bg-[#06B6D4]/30 transition-colors"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    setIsResizingOutline(true);
                    const startX = e.clientX;
                    const startWidth = outlineWidth;

                    const handleMouseMove = (e: MouseEvent) => {
                      const delta = e.clientX - startX;
                      const newWidth = Math.max(200, Math.min(400, startWidth + delta));
                      setOutlineWidth(newWidth);
                    };

                    const handleMouseUp = () => {
                      setIsResizingOutline(false);
                      document.removeEventListener('mousemove', handleMouseMove);
                      document.removeEventListener('mouseup', handleMouseUp);
                    };

                    document.addEventListener('mousemove', handleMouseMove);
                    document.addEventListener('mouseup', handleMouseUp);
                  }}
                />
              </>
            )}

            {/* Bookmarks Sidebar */}
            {showBookmarks && (
              <>
                <div
                  className="glass-pane h-full overflow-hidden flex flex-col"
                  style={{ width: `${bookmarksWidth}px` }}
                >
                  <div className="px-4 py-3 border-b border-white/10 dark:border-white/10 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <h3 className="text-sm font-bold uppercase tracking-wider text-[#64748B] dark:text-[#06B6D4]">
                        Bookmarks
                      </h3>
                      <span className="text-xs px-1.5 py-0.5 rounded-full bg-[#64748B]/20 dark:bg-[#06B6D4]/20 text-[#64748B] dark:text-[#06B6D4]">
                        {bookmarks.length}
                      </span>
                    </div>
                    <button
                      onClick={() => setShowBookmarks(false)}
                      className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                    >
                      <XMarkIcon className="h-4 w-4 text-gray-500" />
                    </button>
                  </div>
                  <div className="flex-1 overflow-y-auto">
                    {bookmarks.length === 0 ? (
                      <div className="flex flex-col items-center justify-center h-full px-4 text-center">
                        <BookmarkIcon className={`h-12 w-12 mb-3 ${
                          isDarkMode ? 'text-slate-600' : 'text-gray-300'
                        }`} />
                        <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                          No bookmarks yet
                        </p>
                        <p className={`text-xs mt-1 ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`}>
                          Click the bookmark icon to save pages
                        </p>
                      </div>
                    ) : (
                      <div className="py-2">
                        {bookmarks.map(bookmark => {
                          const isCurrent = bookmark.page_number === pageNumber;
                          const colorConfig = BOOKMARK_COLORS.find(c => c.name === bookmark.color);

                          return (
                            <button
                              key={bookmark.id}
                              onClick={() => setPageNumber(bookmark.page_number)}
                              className={`w-full px-4 py-2.5 flex items-center gap-3 text-left transition-colors ${
                                isCurrent
                                  ? isDarkMode ? 'bg-cyan-900/30' : 'bg-cyan-50'
                                  : isDarkMode ? 'hover:bg-slate-800/50' : 'hover:bg-gray-50'
                              }`}
                            >
                              <BookmarkSolidIcon className={`h-4 w-4 flex-shrink-0 ${colorConfig?.text || 'text-blue-500'}`} />
                              <div className="min-w-0 flex-1">
                                <div className={`text-sm font-medium ${
                                  isDarkMode ? 'text-white' : 'text-gray-900'
                                }`}>
                                  Page {bookmark.page_number}
                                </div>
                                {bookmark.label && (
                                  <div className={`text-xs truncate ${
                                    isDarkMode ? 'text-slate-400' : 'text-gray-500'
                                  }`}>
                                    {bookmark.label}
                                  </div>
                                )}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>

                {/* Resize Handle */}
                <div
                  className="w-1 cursor-col-resize hover:bg-[#64748B]/30 dark:hover:bg-[#06B6D4]/30 transition-colors"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    setIsResizingBookmarks(true);
                    const startX = e.clientX;
                    const startWidth = bookmarksWidth;

                    const handleMouseMove = (e: MouseEvent) => {
                      const delta = e.clientX - startX;
                      const newWidth = Math.max(200, Math.min(400, startWidth + delta));
                      setBookmarksWidth(newWidth);
                    };

                    const handleMouseUp = () => {
                      setIsResizingBookmarks(false);
                      document.removeEventListener('mousemove', handleMouseMove);
                      document.removeEventListener('mouseup', handleMouseUp);
                    };

                    document.addEventListener('mousemove', handleMouseMove);
                    document.addEventListener('mouseup', handleMouseUp);
                  }}
                />
              </>
            )}
            
            {/* PDF Viewer Container */}
            <div className="flex-1 relative overflow-hidden">
            {/* Navigation Controls - Bottom Center */}
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-white/90 dark:bg-black/90 backdrop-blur-md px-3 py-2 rounded-full flex items-center space-x-2 shadow-xl z-20 border border-[#64748B]/20 dark:border-[#06B6D4]/20">
              <span id="tour-pdf-nav" className="flex items-center space-x-2">
              <button
                onClick={() => setPageNumber(Math.max(1, pageNumber - 1))}
                disabled={pageNumber <= 1}
                className="p-1.5 rounded-full disabled:opacity-50 disabled:cursor-not-allowed transition-all hover:bg-[#64748B]/20 dark:hover:bg-[#06B6D4]/20 text-[#1F2937] dark:text-white"
              >
                <ChevronLeftIcon className="h-3.5 w-3.5" />
              </button>

              <div className="flex items-center space-x-2 text-sm">
                <input
                  type="text"
                  value={pageInputValue}
                  onFocus={(e) => e.target.select()}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '');
                    setPageInputValue(value);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      const page = parseInt(pageInputValue) || 1;
                      const validPage = Math.min(Math.max(1, page), numPages);
                      setPageNumber(validPage);
                      e.currentTarget.blur();
                    }
                  }}
                  onBlur={() => {
                    const page = parseInt(pageInputValue) || 1;
                    const validPage = Math.min(Math.max(1, page), numPages);
                    setPageNumber(validPage);
                    setPageInputValue(validPage.toString());
                  }}
                  className="w-10 text-center bg-transparent border-b border-[#64748B]/50 dark:border-[#06B6D4]/50 text-[#1F2937] dark:text-white font-medium focus:outline-none focus:border-[#64748B] dark:focus:border-[#06B6D4] transition-colors text-sm"
                />
                <span className="text-[#6B7280] dark:text-white/70 text-sm">/</span>
                <span className="text-[#6B7280] dark:text-white/70 text-sm">{numPages || '?'}</span>
              </div>

              <button
                onClick={() => setPageNumber(Math.min(pageNumber + 1, numPages))}
                disabled={pageNumber >= numPages}
                className="p-1.5 rounded-full disabled:opacity-50 disabled:cursor-not-allowed transition-all hover:bg-[#64748B]/20 dark:hover:bg-[#06B6D4]/20 text-[#1F2937] dark:text-white"
              >
                <ChevronRightIcon className="h-3.5 w-3.5" />
              </button>
              </span>

              <div className="w-px h-4 bg-[#64748B]/20 dark:bg-[#06B6D4]/20 mx-2" />

              {/* Bookmark Button with Popover */}
              <BookmarkPopover
                pageNumber={pageNumber}
                isBookmarked={isPageBookmarked(pageNumber)}
                existingLabel={getBookmarkForPage(pageNumber)?.label}
                existingColor={getBookmarkForPage(pageNumber)?.color || 'blue'}
                onSave={async (label, color) => {
                  const existing = getBookmarkForPage(pageNumber);
                  if (existing) {
                    await updateBookmark(existing.id, { label, color });
                  } else {
                    await createBookmark({ page_number: pageNumber, label, color });
                  }
                }}
                onDelete={isPageBookmarked(pageNumber) ? async () => {
                  const existing = getBookmarkForPage(pageNumber);
                  if (existing) {
                    await deleteBookmark(existing.id);
                  }
                } : undefined}
                disabled={bookmarksLoading}
              />

              <div className="w-px h-4 bg-[#64748B]/20 dark:bg-[#06B6D4]/20 mx-2" />

              <span id="tour-zoom" className="flex items-center">
              <button
                onClick={() => setZoomLevel(Math.max(50, zoomLevel - 10))}
                disabled={zoomLevel <= 50}
                className="p-1.5 rounded-full disabled:opacity-50 disabled:cursor-not-allowed transition-all hover:bg-[#64748B]/20 dark:hover:bg-[#06B6D4]/20 text-[#1F2937] dark:text-white"
                title="Zoom Out (Cmd/Ctrl + -)"
              >
                <MagnifyingGlassMinusIcon className="h-3.5 w-3.5" />
              </button>

              <div className="flex items-center gap-1 px-1">
                <span className="text-sm font-medium text-[#1F2937] dark:text-white min-w-[40px] text-center">
                  {zoomLevel}%
                </span>
              </div>

              <button
                onClick={() => setZoomLevel(Math.min(200, zoomLevel + 10))}
                disabled={zoomLevel >= 200}
                className="p-1.5 rounded-full disabled:opacity-50 disabled:cursor-not-allowed transition-all hover:bg-[#64748B]/20 dark:hover:bg-[#06B6D4]/20 text-[#1F2937] dark:text-white"
                title="Zoom In (Cmd/Ctrl + +)"
              >
                <MagnifyingGlassPlusIcon className="h-3.5 w-3.5" />
              </button>

              <button
                onClick={() => setZoomLevel(100)}
                className="ml-1 px-2 py-1 text-xs rounded-md hover:bg-[#64748B]/20 dark:hover:bg-[#06B6D4]/20 text-[#1F2937] dark:text-white"
                title="Reset Zoom (Cmd/Ctrl + 0)"
              >
                Fit
              </button>
              </span>
              
              <div className="w-px h-4 bg-[#64748B]/20 dark:bg-[#06B6D4]/20 mx-2" />
              
              {/* Outline toggle button */}
              {resolvedOutline.length > 0 && (
                <>
                  <button
                    id="tour-contents"
                    onClick={() => setShowOutline(!showOutline)}
                    className={`px-3 py-1 text-xs rounded-md transition-all ${
                      showOutline
                        ? 'bg-[#64748B] dark:bg-[#06B6D4] text-white font-medium'
                        : 'hover:bg-[#64748B]/20 dark:hover:bg-[#06B6D4]/20 text-[#1F2937] dark:text-white'
                    }`}
                    title={showOutline ? "Hide table of contents" : "Show table of contents"}
                  >
                    <span className="inline-flex items-center gap-1.5">
                      <Bars3Icon className="h-3.5 w-3.5" />
                      Contents
                    </span>
                  </button>

                  <div className="w-px h-4 bg-[#64748B]/20 dark:bg-[#06B6D4]/20 mx-2" />
                </>
              )}

              {/* Bookmarks toggle button */}
              <button
                id="tour-bookmarks"
                onClick={() => setShowBookmarks(!showBookmarks)}
                className={`px-3 py-1 text-xs rounded-md transition-all ${
                  showBookmarks
                    ? 'bg-[#64748B] dark:bg-[#06B6D4] text-white font-medium'
                    : 'hover:bg-[#64748B]/20 dark:hover:bg-[#06B6D4]/20 text-[#1F2937] dark:text-white'
                }`}
                title={showBookmarks ? "Hide bookmarks" : "Show bookmarks"}
              >
                <span className="inline-flex items-center gap-1.5">
                  <BookmarkIcon className="h-3.5 w-3.5" />
                  Bookmarks
                  {bookmarks.length > 0 && (
                    <span className={`px-1.5 py-0.5 rounded text-[10px] ${
                      showBookmarks ? 'bg-white/20' : 'bg-[#64748B]/20 dark:bg-[#06B6D4]/20'
                    }`}>
                      {bookmarks.length}
                    </span>
                  )}
                </span>
              </button>


            </div>
            
            
            {/* Snipping Selection Overlay */}
            {isSnipping && snipStart && snipEnd && (() => {
              const pageElement = document.querySelector('.react-pdf__Page') as HTMLElement;
              if (!pageElement) return null;
              
              const pageRect = pageElement.getBoundingClientRect();
              const containerRect = pdfContentRef.current?.getBoundingClientRect();
              if (!containerRect) return null;
              
              return (
                <div 
                  className="absolute border-2 border-[#06B6D4] bg-[#06B6D4]/20 pointer-events-none z-30"
                  style={{
                    left: pageRect.left - containerRect.left + Math.min(snipStart.x, snipEnd.x),
                    top: pageRect.top - containerRect.top + Math.min(snipStart.y, snipEnd.y),
                    width: Math.abs(snipEnd.x - snipStart.x),
                    height: Math.abs(snipEnd.y - snipStart.y)
                  }}
                />
              );
            })()}
            
            {/* Page Change Indicator */}
            {showPageIndicator && (
              <div className="absolute top-20 left-1/2 transform -translate-x-1/2 glass-pane px-6 py-3 shadow-lg transition-opacity duration-300 z-20">
                <span className="text-2xl font-bold text-[#1F2937] dark:text-[#E2E8F0]">
                  {pageNumber} / {numPages}
                </span>
              </div>
            )}
            
            {/* Navigation Hint - positioned above bottom toolbar */}
            {showScrollHint && numPages > 1 && (
              <div className="absolute bottom-24 left-1/2 transform -translate-x-1/2 glass-pane px-4 py-2 shadow-lg animate-bounce z-20">
                <div className="flex items-center gap-2 text-sm text-[#6B7280] dark:text-[#94A3B8]">
                  <span>Use ← → arrow keys to navigate</span>
                </div>
              </div>
            )}
            
            <div
              id="tour-text-highlight"
              className="w-full h-full overflow-auto flex justify-center items-start p-4 bg-[#F6F8FA] dark:bg-[#030712]"
              onMouseUp={(e) => {
                if (isSnipping) {
                  handleMouseUp();
                  return;
                }

                // Check for text selection after mouse up
                const selection = window.getSelection();
                if (selection && selection.toString().trim().length > 0) {
                  handleTextSelection(e);
                }
              }}
              onMouseDown={isSnipping ? handleMouseDown : undefined}
              onMouseMove={isSnipping ? handleMouseMove : undefined}
            >
              {pdfUrl && (
                  <Document
                    key={pdfKey}
                    file={pdfUrl}
                    onLoadSuccess={onDocumentLoadSuccess}
                    onLoadError={onDocumentLoadError}
                    onItemClick={({ pageNumber: targetPage, dest }) => {
                      // Handle internal PDF navigation
                      if (targetPage && typeof targetPage === 'number') {
                        setPageNumber(targetPage);
                        // Show page indicator briefly
                        setShowPageIndicator(true);
                        setTimeout(() => setShowPageIndicator(false), 1500);
                      } else if (dest) {
                        // Handle named destinations or other link types
                        console.log('Navigation to destination:', dest);
                      }
                    }}
                    loading={
                      <div className="flex items-center justify-center p-8">
                        <ArrowPathIcon className="h-8 w-8 text-[#64748B] dark:text-[#06B6D4] animate-spin" />
                      </div>
                    }
                    error={
                      <div className="flex items-center justify-center p-8">
                        <p className="text-red-600">Failed to load PDF. Please try refreshing the page.</p>
                      </div>
                    }
                    externalLinkTarget="_blank"
                    externalLinkRel="noopener noreferrer"
                  >
                    <div className="relative">
                    <Page
                        key={`${pageNumber}-${zoomLevel}`}
                      pageNumber={pageNumber}
                        width={pdfContentRef.current?.clientWidth ? (pdfContentRef.current.clientWidth - 80) * (zoomLevel / 100) : 800 * (zoomLevel / 100)}
                        className={`pdf-page-transition shadow-xl ${isSnipping ? 'cursor-crosshair' : ''}`}
                      renderTextLayer={true}
                      renderAnnotationLayer={true}
                    />
                      
                      {/* Snipping selection box */}
                      {isSnipping && snipStart && snipEnd && (
                        <div
                          className="snip-selection"
                          style={{
                            left: Math.min(snipStart.x, snipEnd.x),
                            top: Math.min(snipStart.y, snipEnd.y),
                            width: Math.abs(snipEnd.x - snipStart.x),
                            height: Math.abs(snipEnd.y - snipStart.y)
                          }}
                        />
                      )}
                </div>
                  </Document>
              )}
            </div>
          </div>

          {/* TL;DR Modal - Centered */}
          {showSummary && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
              {/* Backdrop */}
              <div
                className="absolute inset-0 bg-black/30 dark:bg-black/70 backdrop-blur-sm"
                onClick={() => setShowSummary(false)}
              />

              {/* Modal with Arctic Frost Glass Effect */}
              <div className="relative w-full max-w-3xl max-h-[85vh] bg-white/95 dark:bg-slate-900/95 backdrop-blur-3xl rounded-3xl shadow-[0_20px_60px_-15px_rgba(0,0,0,0.15)] dark:shadow-[0_20px_60px_-15px_rgba(0,0,0,0.5),0_0_40px_rgba(6,182,212,0.1)] border border-slate-200 dark:border-slate-700/60 flex flex-col overflow-hidden">
                {/* Header */}
                <div className="px-6 py-4 flex justify-between items-center border-b border-slate-200 dark:border-slate-700/80 bg-slate-50/80 dark:bg-slate-800/60 backdrop-blur-xl">
                <div className="flex items-center gap-2">
                  <h3 className="text-base font-bold text-slate-900 dark:text-white drop-shadow-sm">
                    TL;DR - Page {pageNumber}
                  </h3>
                  {!tldrLoading && tldrData && (
                    <button
                      onClick={() => fetchTLDR(true)}
                      className="p-1 hover:bg-slate-200 dark:hover:bg-slate-700/50 rounded transition-colors"
                      title="Refresh TL;DR"
                    >
                      <ArrowPathIcon className="h-4 w-4 text-slate-600 dark:text-slate-400" />
                    </button>
                  )}
                  <LanguageToggle
                    overrideLang={tldrLangOverride}
                    onToggle={setTldrLangOverride}
                    isDarkMode={isDarkMode}
                    size="xs"
                  />
                </div>
                <button
                  onClick={() => setShowSummary(false)}
                  className="p-1.5 hover:bg-slate-200 dark:hover:bg-slate-700/50 rounded-full transition-colors"
                >
                  <XMarkIcon className="h-5 w-5 text-slate-700 dark:text-slate-300" />
                </button>
              </div>

                {/* Content */}
                <div className="flex-grow p-6 overflow-y-auto" style={{ overscrollBehavior: 'contain' }}>
                {tldrLoading ? (() => {
                  // If the EN toggle has forced English, don't claim we're
                  // translating to the user's pref language — that'd be a lie.
                  const showingEnglishOverride = tldrLangOverride === 'english';
                  const showTranslatingMsg = userLangDisplay && !showingEnglishOverride;
                  return (
                    <div className="flex items-center justify-center h-full">
                      <div className="text-center">
                        <ArrowPathIcon className="h-8 w-8 animate-spin mx-auto mb-2 text-cyan-500" />
                        <p className="text-base font-medium text-slate-900 dark:text-white">
                          {showTranslatingMsg
                            ? `Translating to ${userLangDisplay}...`
                            : 'Brewing your page summary...'}
                        </p>
                        <p className="text-xs mt-1 text-slate-600 dark:text-slate-400">
                          {showTranslatingMsg
                            ? 'First read takes a moment — next time is instant ✨'
                            : `${["Distilling key concepts", "Extracting the good stuff", "Condensing wisdom", "Summarizing brilliance"][Math.floor(Math.random() * 4)]} ✨`}
                        </p>
                      </div>
                    </div>
                  );
                })() : tldrData ? (
                  <div className="space-y-5">
                    {/* Summary - Cyan/Blue theme */}
                    {tldrData.tldr?.summary && (
                      <div className="bg-gradient-to-br from-cyan-50 via-sky-50 to-blue-50 dark:from-cyan-900/30 dark:via-sky-900/20 dark:to-blue-900/30 p-5 rounded-xl border-l-4 border-cyan-500 dark:border-cyan-400 shadow-md dark:shadow-cyan-500/5">
                        <div className="flex items-start gap-2 mb-2">
                          <BookOpenIcon className="h-5 w-5 text-cyan-600 dark:text-cyan-400 flex-shrink-0 mt-0.5" />
                          <h4 className="font-bold text-slate-900 dark:text-white">Summary</h4>
                        </div>
                        <div className="text-sm leading-relaxed text-slate-800 dark:text-slate-200 [&_p]:m-0">
                          <MarkdownRenderer content={tldrData.tldr.summary} />
                        </div>
                      </div>
                    )}

                    {/* One-line Takeaway - Amber/Yellow accent */}
                    {tldrData.tldr?.one_line_takeaway && (
                      <div className="bg-gradient-to-br from-amber-50 via-yellow-50 to-orange-50 dark:from-amber-900/20 dark:via-yellow-900/15 dark:to-orange-900/20 p-4 rounded-xl border-l-4 border-amber-500 dark:border-amber-400 shadow-md">
                        <div className="text-sm font-medium text-slate-800 dark:text-slate-200 flex items-start gap-2">
                          <span className="text-lg flex-shrink-0">💡</span>
                          <div className="[&_p]:m-0">
                            <MarkdownRenderer content={tldrData.tldr.one_line_takeaway} />
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Key Concepts - Cyan theme (replaced purple) */}
                    {tldrData.tldr?.key_concepts && tldrData.tldr.key_concepts.length > 0 && (
                      <div>
                        <div className="flex items-center gap-2 mb-3">
                          <SparklesIcon className="h-5 w-5 text-cyan-600 dark:text-cyan-400" />
                          <h4 className="font-bold text-slate-900 dark:text-white">Key Concepts</h4>
                        </div>
                        <div className="space-y-3">
                          {tldrData.tldr.key_concepts.map((item: any, idx: number) => (
                            <div
                              key={idx}
                              className="bg-gradient-to-br from-cyan-50/80 via-white to-sky-50/80 dark:from-cyan-900/20 dark:via-slate-800/50 dark:to-sky-900/20 p-4 rounded-xl border border-cyan-200 dark:border-cyan-700/50 shadow-sm"
                            >
                              {typeof item === 'string' ? (
                                <div className="text-sm text-slate-800 dark:text-slate-200 [&_p]:m-0">
                                  <MarkdownRenderer content={item} />
                                </div>
                              ) : (
                                <>
                                  <div className="font-semibold text-cyan-700 dark:text-cyan-300 mb-1.5 [&_p]:m-0 [&_p]:font-semibold">
                                    <MarkdownRenderer content={item.concept} />
                                  </div>
                                  <div className="text-sm text-slate-700 dark:text-slate-300 [&_p]:m-0">
                                    <MarkdownRenderer content={item.definition} />
                                  </div>
                                </>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Practical Examples - Emerald/Teal theme */}
                    {tldrData.tldr?.practical_examples && tldrData.tldr.practical_examples.length > 0 && (
                      <div>
                        <div className="flex items-center gap-2 mb-3">
                          <span className="text-xl">📝</span>
                          <h4 className="font-bold text-slate-900 dark:text-white">Examples</h4>
                        </div>
                        <div className="space-y-3">
                          {tldrData.tldr.practical_examples.map((item: any, idx: number) => (
                            <div
                              key={idx}
                              className="bg-gradient-to-br from-emerald-50/80 via-white to-teal-50/80 dark:from-emerald-900/20 dark:via-slate-800/50 dark:to-teal-900/20 p-4 rounded-xl border border-emerald-200 dark:border-emerald-700/50 shadow-sm"
                            >
                              {typeof item === 'string' ? (
                                <div className="text-sm text-slate-800 dark:text-slate-200 [&_p]:m-0">
                                  <MarkdownRenderer content={item} />
                                </div>
                              ) : (
                                <>
                                  <div className="font-semibold text-emerald-700 dark:text-emerald-300 mb-1.5 [&_p]:m-0 [&_p]:font-semibold">
                                    <MarkdownRenderer content={item.concept} />
                                  </div>
                                  <div className="text-sm text-slate-700 dark:text-slate-300 [&_p]:m-0">
                                    <MarkdownRenderer content={item.example} />
                                  </div>
                                </>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Memory Aids - Blue theme (replaced pink) */}
                    {tldrData.tldr?.memory_aids && tldrData.tldr.memory_aids.length > 0 && (
                      <div>
                        <div className="flex items-center gap-2 mb-3">
                          <span className="text-xl">🧠</span>
                          <h4 className="font-bold text-slate-900 dark:text-white">Memory Aids</h4>
                        </div>
                        <div className="space-y-3">
                          {tldrData.tldr.memory_aids.map((item: any, idx: number) => (
                            <div
                              key={idx}
                              className="bg-gradient-to-br from-blue-50/80 via-white to-indigo-50/80 dark:from-blue-900/20 dark:via-slate-800/50 dark:to-indigo-900/20 p-4 rounded-xl border border-blue-200 dark:border-blue-700/50 shadow-sm"
                            >
                              {typeof item === 'string' ? (
                                <div className="text-sm text-slate-800 dark:text-slate-200 [&_p]:m-0">
                                  <MarkdownRenderer content={item} />
                                </div>
                              ) : (
                                <>
                                  <div className="font-semibold text-blue-700 dark:text-blue-300 mb-1.5 [&_p]:m-0 [&_p]:font-semibold">
                                    <MarkdownRenderer content={item.concept} />
                                  </div>
                                  <div className="text-sm text-slate-700 dark:text-slate-300 [&_p]:m-0">
                                    <MarkdownRenderer content={item.technique} />
                                  </div>
                                </>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Content Info */}
                    <div className="pt-3 border-t border-slate-200 dark:border-slate-700 text-xs text-slate-600 dark:text-slate-400">
                      <div className="flex flex-wrap gap-2">
                        {tldrData.difficulty_level && (
                          <span className="px-3 py-1.5 bg-slate-100 dark:bg-slate-800/70 rounded-lg font-medium">
                            Difficulty: {tldrData.difficulty_level}
                          </span>
                        )}
                        {tldrData.content_type && (
                          <span className="px-3 py-1.5 bg-slate-100 dark:bg-slate-800/70 rounded-lg font-medium">
                            Type: {tldrData.content_type}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-full text-center">
                    <p className="text-slate-600 dark:text-slate-400">
                      No summary available for this page.
                    </p>
                  </div>
                )}
                </div>
              </div>
            </div>
          )}

            {/* Pulse Check — glass overlay on PDF area with comprehension checks */}
            {/* Triggers: auto (10-page toast) OR manual (bolt button) */}
            {/* Guard: don't show during active chat streaming */}
            {((showProgressToast && toastConcepts.length > 0) || manualPulseCheck) && !streamingMessageId && !isThinking && (
              <PulseCheckCard
                bookId={bookIdStr}
                pagesVisited={pagesVisited}
                readingSessionId={sessionId}
                onDismiss={() => {
                  handleToastDismiss();
                  setManualPulseCheck(false);
                }}
                onPulseActive={setIsPulseActive}
                isDarkMode={isDarkMode}
              />
            )}

            </div> {/* End of PDF Viewer Container */}
          </div> {/* End of PDF Content */}

        {/* Vertical Resizer */}
        <div
          className="sidebar-resizer"
          onMouseDown={(e) => {
            e.preventDefault();
            setIsResizingSidebar(true);
            const startX = e.clientX;
            const startWidth = sidebarWidth;

            const handleMouseMove = (e: MouseEvent) => {
              const deltaX = startX - e.clientX;
              const newWidth = Math.min(600, Math.max(300, startWidth + deltaX));
              setSidebarWidth(newWidth);
            };

            const handleMouseUp = () => {
              setIsResizingSidebar(false);
              document.removeEventListener('mousemove', handleMouseMove);
              document.removeEventListener('mouseup', handleMouseUp);
            };

            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
          }}
        />

        {/* Right Pane - AI Sidebar */}
        {isSidebarVisible && (
          <div className="h-full flex flex-col transition-all duration-300" style={{ width: `${sidebarWidth}px`, minWidth: `${sidebarWidth}px` }}>
          <div id="tour-sidebar" className="w-full h-full flex flex-col glass-pane overflow-hidden">
              {/* Sidebar Header with Close Button */}
            <div className="flex-shrink-0 px-4 py-3 border-b border-white/10 dark:border-white/10">
                <div className="flex items-center justify-end">
                  <button
                    onClick={() => {
                      setIsSidebarVisible(false);
                      setPdfKey(prev => prev + 1); // Force PDF re-render
                    }}
                    className="p-1.5 hover:bg-[#64748B]/10 dark:hover:bg-[#06B6D4]/10 rounded-lg transition-colors text-[#6B7280] dark:text-[#94A3B8] hover:text-[#1F2937] dark:hover:text-[#E2E8F0]"
                    data-tooltip-id="dhyana-tooltip"
                    data-tooltip-content="Close sidebar (Cmd/Ctrl + B)"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>
              
            {/* Tab Switcher */}
              <div className="flex-shrink-0 px-4 py-2 border-b border-white/10 dark:border-white/10">
                <div className="flex items-center bg-[#64748B]/10 dark:bg-[#06B6D4]/15 rounded-lg p-1">
                <button
                  id="tour-chat-tab"
                  onClick={() => setActiveTab('chat')}
                  className={`w-1/2 py-2 text-sm font-semibold rounded-md transition-all duration-200 ${
                    activeTab === 'chat'
                        ? 'bg-[#64748B]/20 dark:bg-[#06B6D4]/25 text-[#1F2937] dark:text-[#E2E8F0]'
                        : 'text-[#6B7280] dark:text-[#94A3B8]'
                  }`}
                  data-tooltip-id="dhyana-tooltip"
                  data-tooltip-content="Ask questions about the content"
                >
                  Chat
                </button>
                <button
                  id="tour-thoughtpad-tab"
                  onClick={() => setActiveTab('thoughtpad')}
                  className={`w-1/2 py-2 text-sm font-semibold rounded-md transition-all duration-200 ${
                    activeTab === 'thoughtpad'
                        ? 'bg-[#64748B]/20 dark:bg-[#06B6D4]/25 text-[#1F2937] dark:text-[#E2E8F0]'
                        : 'text-[#6B7280] dark:text-[#94A3B8]'
                  }`}
                  data-tooltip-id="dhyana-tooltip"
                  data-tooltip-content="View your notes, highlights, and thoughts"
                >
                  ThoughtPad
                </button>
              </div>
            </div>
            
            {/* Chat Sessions Header */}
            {activeTab === 'chat' && (
              <div className="flex-shrink-0 p-3 border-b border-white/10 dark:border-white/10">
                <div className="flex items-center justify-between">
                  <div className="relative flex items-center gap-1 flex-1">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowSessionMenu(!showSessionMenu);
                      }}
                      className="flex items-center gap-2 text-sm font-medium text-[#1F2937] dark:text-[#E2E8F0] hover:bg-[#64748B]/10 dark:hover:bg-[#06B6D4]/10 px-3 py-1.5 rounded-lg transition-colors flex-1"
                    >
                      <ChatBubbleLeftRightIcon className="h-4 w-4" />
                      <span className="flex-1 text-left">{currentSession?.title || currentSession?.session_name || 'New Chat'}</span>
                      <ChevronDownIcon className="h-3 w-3" />
                    </button>
                    
                    {showSessionMenu && (
                      <div className="absolute top-full left-0 mt-1 w-48 glass-pane rounded-lg shadow-xl z-10 flex flex-col max-h-[400px]">
                        <div className="overflow-y-auto p-1 flex-1">
                        {chatSessions.map(session => (
                          <button
                            key={session.id}
                            onClick={async () => {
                              await activateSession(session.id);
                              setShowSessionMenu(false);
                            }}
                            className={`w-full px-3 py-2 text-sm rounded-md text-left transition-colors ${
                              session.is_active
                                ? 'bg-[#64748B]/20 dark:bg-[#06B6D4]/20 text-[#1F2937] dark:text-[#E2E8F0]'
                                : 'hover:bg-[#64748B]/10 dark:hover:bg-[#06B6D4]/10 text-[#6B7280] dark:text-[#94A3B8]'
                            }`}
                          >
                            <div className="font-medium truncate">
                              {session.title || session.session_name}
                            </div>
                          </button>
                        ))}
                        </div>
                        <div className="border-t border-white/10 dark:border-white/10 p-1 flex-shrink-0">
                          <button
                            onClick={createNewSession}
                            className="block w-full px-3 py-2 text-sm rounded-md text-left hover:bg-[#64748B]/10 dark:hover:bg-[#06B6D4]/10 text-[#64748B] dark:text-[#06B6D4] font-medium"
                          >
                            <div className="flex items-center gap-2">
                              <PlusIcon className="h-4 w-4" />
                              New Chat
                            </div>
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <span className="text-xs text-[#6B7280] dark:text-[#94A3B8]">
                    {chatSessions.length} session{chatSessions.length !== 1 ? 's' : ''}
                  </span>
                </div>
              </div>
            )}

            {/* Chat View */}
            {activeTab === 'chat' && (
              <div className="flex flex-col flex-1 min-h-0">
                {/* Chat Area */}
                <div 
                  ref={chatAreaRef} 
                  className="flex-grow p-4 space-y-4 overflow-y-auto"
                  onScroll={(e) => {
                    const target = e.currentTarget;
                    // Load more messages when scrolled to top
                    if (target.scrollTop < 100 && hasMore && !loadingMore) {
                      loadMoreMessages();
                    }
                  }}
                >
                  {/* Loading more indicator */}
                  {loadingMore && (
                    <div className="text-center py-2">
                      <div className="inline-flex items-center gap-2 text-sm text-[#6B7280] dark:text-[#94A3B8]">
                        <div className="w-4 h-4 border-2 border-[#64748B] dark:border-[#06B6D4] border-t-transparent animate-spin rounded-full"></div>
                        Loading older messages...
                      </div>
                    </div>
                  )}
                  
                  {/* Messages */}
                  {messagesLoading ? (
                    <div className="flex items-center justify-center h-full">
                      <div className="text-center">
                        <div className="w-8 h-8 border-2 border-[#64748B] dark:border-[#06B6D4] border-t-transparent animate-spin rounded-full mx-auto mb-2"></div>
                        <p className="text-sm text-[#6B7280] dark:text-[#94A3B8]">Loading messages...</p>
                      </div>
                    </div>
                  ) : messages.length === 0 ? (
                    <div className="flex items-center justify-center h-full">
                      <div className="text-center">
                        <div className="mx-auto mb-3">
                          <SparkleIcon size={48} color="#06B6D4" animated={true} />
                        </div>
                        <p className="text-[#1F2937] dark:text-[#E2E8F0] font-medium">Clarity begins with a question. Ask away.</p>
                        <p className="text-xs text-slate-400 dark:text-slate-500 mt-3">
                          Make the most of your reader — see our{' '}
                          <a
                            href="/howtoread/"
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => trackEvent('reader_guide_click', { source: 'reader_empty_state' })}
                            className="text-cyan-500 hover:underline"
                          >
                            quick guide
                          </a>{' '}→
                        </p>
                      </div>
                    </div>
                  ) : (
                    messages.map((message, index) => (
                    <div key={message.id}>
                      {message.message_type === 'user' ? (
                        <div className="flex items-start gap-3 justify-end">
                          <div
                            className={`bg-cyan-600 dark:bg-[#06B6D4] text-white rounded-xl p-3 ${message.attachments && message.attachments.length > 0 ? 'max-w-[85%]' : 'max-w-xs md:max-w-sm'} transition-all shadow-sm`}
                          >
                            {(() => {
                              const hasAttachments = message.attachments && message.attachments.length > 0;
                              const { truncated, isTruncated } = message.content_text ? truncateText(message.content_text) : { truncated: '', isTruncated: false };
                              const needsExpand = isTruncated || hasAttachments;
                              const isExpanded = expandedTextMessages.has(message.id);
                              const collapsedLabel = isTruncated ? 'Read more' : `${message.attachments?.length} ${message.attachments?.length === 1 ? 'attachment' : 'attachments'}`;

                              return (
                                <>
                                  {/* Single expand/collapse toggle */}
                                  {needsExpand && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        toggleTextExpansion(message.id);
                                      }}
                                      className="flex items-center gap-1 text-xs mb-2 opacity-70 hover:opacity-100 transition-opacity bg-transparent border-none text-white cursor-pointer p-0"
                                    >
                                      {isExpanded ? (
                                        <>
                                          <ChevronUpIcon className="h-3 w-3" />
                                          <span>Minimize</span>
                                        </>
                                      ) : (
                                        <>
                                          <ChevronDownIcon className="h-3 w-3" />
                                          <span>{collapsedLabel}</span>
                                        </>
                                      )}
                                    </button>
                                  )}

                                  {/* Text content */}
                                  {message.content_text && (
                                    <p className="text-sm font-medium">
                                      {isExpanded || !isTruncated ? message.content_text : truncated}
                                    </p>
                                  )}

                                  {/* Attachments */}
                                  {hasAttachments && (
                                    <>
                                      {!isExpanded ? (
                                        <div className="mt-2">
                                          <img
                                            src={message.attachments![0].data_url}
                                            alt="Attachment preview"
                                            className="w-16 h-16 rounded object-cover cursor-pointer hover:opacity-90 transition-opacity"
                                            onClick={(e) => handleMessageAttachmentClick(e, message, 0)}
                                            title="Click to view full size"
                                          />
                                        </div>
                                      ) : (
                                        <div className="mt-3">
                                          <div className="grid grid-cols-2 gap-2 animate-fadeIn">
                                            {message.attachments!.map((attachment, index) => (
                                              <div
                                                key={index}
                                                className="relative group"
                                                style={{ animationDelay: `${index * 50}ms` }}
                                              >
                                                <img
                                                  src={attachment.data_url}
                                                  alt={`Page ${attachment.page_number}`}
                                                  className="w-full rounded-lg shadow-md cursor-pointer hover:scale-105 transition-transform"
                                                  onClick={(e) => handleMessageAttachmentClick(e, message, index)}
                                                  title="Click to view full size"
                                                />
                                                {(attachment.page_number || attachment.file_name) && (
                                                  <span className="absolute bottom-2 right-2 bg-black/70 backdrop-blur text-white text-xs px-2 py-1 rounded opacity-90 group-hover:opacity-100 transition-opacity">
                                                    {attachment.page_number ? `Page ${attachment.page_number}` : attachment.file_name}
                                                  </span>
                                                )}
                                              </div>
                                            ))}
                                          </div>
                                        </div>
                                      )}
                                    </>
                                  )}
                                </>
                              );
                            })()}
                          </div>
                        </div>
                      ) : message.message_type === 'ai' ? (
                        // Skip rendering if content is empty (let ThinkingIndicator handle loading state)
                        !message.content_text && message.content_text !== '...' ? null : (
                        <>
                        <div className="flex items-start gap-3">
                          <div className="w-8 h-8 flex-shrink-0 flex items-center justify-center">
                            <SparkleIcon size={24} color="#06B6D4" animated={true} />
                          </div>
                          <div className="bg-white dark:bg-slate-800/60 rounded-xl p-3 flex-1 min-w-0 shadow-sm border border-slate-200/80 dark:border-slate-700/50">
                            {message.content_text === '...' ? (
                              <div className="flex items-center gap-1 py-1">
                                <div className="w-2 h-2 bg-cyan-500 dark:bg-[#06B6D4] rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                                <div className="w-2 h-2 bg-cyan-500 dark:bg-[#06B6D4] rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                                <div className="w-2 h-2 bg-cyan-500 dark:bg-[#06B6D4] rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                              </div>
                            ) : (
                              <>
                                {(() => {
                                  const isStreaming = message.id === streamingMessageId;

                                  return (
                                    <div>
                                      <CollapsibleMarkdown
                                        content={message.content_text}
                                        className="text-sm text-slate-800 dark:text-[#E2E8F0]"
                                        onImageClick={(src) => setZoomedImageUrl(src)}
                                        onPageLinkClick={handlePageLinkClick}
                                        onNoteClick={scrollToNote}
                                        isStreaming={isStreaming}
                                      />
                                      {isStreaming && message.content_text && (
                                        <span className="inline-flex gap-1 ml-1">
                                          <span className="w-1.5 h-1.5 bg-cyan-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                                          <span className="w-1.5 h-1.5 bg-cyan-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                                          <span className="w-1.5 h-1.5 bg-cyan-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                                        </span>
                                      )}
                                    </div>
                                  );
                                })()}
                                <div className="mt-3 border-t border-slate-200 dark:border-white/10 pt-2">
                                  <button
                                    onClick={() => addToThoughtpad(
                                      message.content_text.replace(/<[^>]*>/g, ''),
                                      'chat_answer', // AI responses should be marked as chat_answer
                                      [], // no images
                                      [], // tag as AI generated
                                      {
                                        ai_response: true,
                                        from_chat: true,
                                        chat_message_id: message.id  // AI answer message ID - backend will derive source_question_id
                                      }
                                    )}
                                    className="text-xs font-semibold text-cyan-600 dark:text-[#06B6D4] hover:opacity-80 transition-opacity flex items-center gap-1.5"
                                    data-tooltip-id="dhyana-tooltip"
                                    data-tooltip-content="Save this AI answer to your ThoughtPad"
                                  >
                                    <PlusIcon className="h-3 w-3" /> Add to ThoughtPad
                                  </button>
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                        {/* Exam cheat codes chip — only for QA-mode responses.
                            Revision output (Concept Review, Speed Run, Full
                            Chapter, Note Review) is already exam-prep format
                            so the chip is redundant there. QA-mode detected
                            by presence of ANY QA signature section (Answer,
                            Connections, Visual Guide, Your turn, Cheat) —
                            follow-up QA responses may open with Connections
                            instead of Answer, so we can't gate on first
                            section alone. */}
                        {(() => {
                          const parsed = parseMarkdownSections(message.content_text || '');
                          const isStreamingMsg = message.id === streamingMessageId;
                          const isLastAi = !messages.slice(index + 1).some(m => m.message_type === 'ai');
                          const hasQAMarker = parsed.sections.some((s) => {
                            const t = s.title.toLowerCase();
                            return (
                              t.includes('answer') ||
                              t.includes('connection') ||
                              t.includes('visual') ||
                              t === 'your turn' ||
                              t.includes('cheat')
                            );
                          });
                          return parsed.hasMultipleSections && !isStreamingMsg && isLastAi && hasQAMarker ? (
                            <button
                              onClick={() => handleSendMessage('Help me with exam cheat codes for this topic 🎯')}
                              className="exam-cheat-codes-chip ml-11 mt-1"
                            >
                              Want to explore exam cheat codes for this topic? 🎯
                            </button>
                          ) : null;
                        })()}
                        </>)
                      ) : (
                        <div className="text-center text-xs text-[#6B7280] dark:text-[#94A3B8] py-2">
                          <p dangerouslySetInnerHTML={{ __html: message.content_text }} />
                        </div>
                      )}
                    </div>
                  ))
                  )}

                  {/* Show thinking indicator while waiting for AI response */}
                  {/* Only show when viewing the session that's currently streaming */}
                  {isThinking && activeSessionId === streamingSessionId && <ThinkingIndicator agentStatus={agentStatus} />}
                </div>

                {/* Chat Input */}
                <div className="flex-shrink-0 p-3 border-t border-slate-200 dark:border-slate-700/50">
                  {/* Contextual prompts - only show when no messages and not thinking */}
                  {messages.length === 0 && !isThinking && (
                    <ChatContextualPrompts
                      pageNumber={pageNumber}
                      chapterName={tldrData?.chapter_name || null}
                      keyConcept={currentConcept}
                      onPromptClick={(prompt) => setChatInput(prompt)}
                    />
                  )}

                  {/* Attachments */}
                  {attachments.length > 0 && (
                    <div className="mb-3 flex flex-wrap gap-2">
                      {attachments.map((attachment, index) => (
                        <div
                          key={index}
                          className="relative group"
                          onMouseEnter={() => setHoveredAttachment(String(index))}
                          onMouseLeave={() => setHoveredAttachment(null)}
                        >
                          <div className="relative w-16 h-16 rounded-lg overflow-hidden border-2 border-cyan-200 dark:border-[#06B6D4]/20 bg-white dark:bg-black/50">
                            <img 
                              src={attachment.data_url} 
                              alt="PDF snippet" 
                              className="w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-black/10 dark:bg-white/10 flex items-center justify-center">
                              <PhotoIcon className="h-4 w-4 text-white/70" />
                            </div>
                            {attachment.type === 'image' && attachment.file_name && (
                              <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-[10px] px-1 py-0.5 truncate">
                                {attachment.file_name}
                              </div>
                            )}
                          </div>
                          <button
                            onClick={() => {
                              setAttachments(prev => prev.filter((_, i) => i !== index));
                            }}
                            className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <XMarkIcon className="h-3 w-3" />
                          </button>
                          
                          {/* Preview tooltip */}
                          {hoveredAttachment === String(index) && (
                            <div className="absolute bottom-full left-0 mb-2 p-2 bg-white dark:bg-black rounded-lg shadow-xl z-50 pointer-events-none">
                              <img 
                                src={attachment.data_url} 
                                alt="Preview" 
                                className="max-w-[200px] max-h-[200px]"
                              />
                              <p className="text-xs text-[#6B7280] dark:text-[#94A3B8] mt-1">
                                {attachment.type === 'snippet' ? `Page ${attachment.page_number}` : attachment.file_name}
                              </p>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  
                  <div className="relative">
                    <textarea
                      ref={chatInputRef}
                      value={chatInput}
                      onChange={(e) => !isPulseActive && setChatInput(e.target.value)}
                      disabled={isPulseActive}
                      onKeyDown={(e) => {
                        if (isPulseActive) return;
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleSendMessage();
                        } else if (e.key === 'Escape') {
                          e.preventDefault();
                          setChatInput('');
                        }
                      }}
                      className={`w-full bg-white dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-lg p-3 pr-40 text-slate-800 dark:text-[#E2E8F0] placeholder-slate-400 dark:placeholder-[#94A3B8] focus:ring-2 focus:ring-cyan-500/30 dark:focus:ring-[#06B6D4]/30 focus:border-cyan-500 dark:focus:border-[#06B6D4] transition resize-none overflow-y-auto ${isPulseActive ? 'opacity-50 cursor-not-allowed' : ''}`}
                      style={{ minHeight: '120px', maxHeight: '300px' }}
                      placeholder={isPulseActive ? 'Chat resumes after Mnemo...' : dynamicPlaceholder}
                    />
                    <div className="absolute right-3 top-3 flex items-center gap-2">
                      {chatInput.trim() && (
                        <button
                          onClick={() => setChatInput('')}
                          className="p-2 hover:bg-red-500/10 rounded-lg transition-colors text-red-500"
                          title="Clear text (Escape)"
                        >
                          <XMarkIcon className="h-5 w-5" />
                        </button>
                      )}

                      {/* Snipping Tool Button */}
                      <button
                        id="tour-snip"
                        onClick={handleStartSnipping}
                        className={`p-2 rounded-lg transition-all ${
                          isSnipping
                            ? 'bg-cyan-500 dark:bg-[#06B6D4] text-white ring-2 ring-cyan-500 dark:ring-[#06B6D4]'
                            : 'hover:bg-cyan-100 dark:hover:bg-[#06B6D4]/10 text-cyan-600 dark:text-[#06B6D4]'
                        }`}
                        data-tooltip-id="dhyana-tooltip"
                        data-tooltip-content={isSnipping ? "Click and drag on PDF to select area (ESC to cancel)" : "Snip PDF area"}
                      >
                        <ScissorsIcon className="h-5 w-5" />
                      </button>

                      <input
                        type="file"
                        id="file-upload"
                        className="hidden"
                        accept="image/*"
                        multiple
                        onChange={handleFileUpload}
                      />
                      <button
                        id="tour-attachments"
                        type="button"
                        onClick={() => document.getElementById('file-upload')?.click()}
                        className="p-2 hover:bg-cyan-100 dark:hover:bg-[#06B6D4]/10 rounded-lg cursor-pointer transition-colors text-cyan-600 dark:text-[#06B6D4]"
                        data-tooltip-id="dhyana-tooltip"
                        data-tooltip-content="Upload images to include in your message"
                      >
                        <PaperClipIcon className="h-5 w-5" />
                      </button>
                    <button
                      onClick={() => handleSendMessage()}
                        disabled={!activeSessionId || isPulseActive || (!chatInput.trim() && attachments.length === 0) || isThinking || streamingMessageId !== null}
                        className="bg-[#06B6D4] dark:bg-[#06B6D4] hover:bg-[#0891B2] dark:hover:bg-[#0891B2] text-white rounded-lg p-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <PaperAirplaneIcon className="h-5 w-5" />
                    </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ThoughtPad View */}
            {activeTab === 'thoughtpad' && (
              <div className="flex flex-col flex-1 min-h-0">
                <div className="flex-1 overflow-y-auto p-4">
                  {notesLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <ArrowPathIcon className="h-6 w-6 text-cyan-500 dark:text-[#06B6D4] animate-spin" />
                    </div>
                  ) : pageNotes && pageNotes.length > 0 ? (
                    <div className="space-y-4">
                      {pageNotes.map((note) => (
                        <div
                          key={note.id}
                          id={`note-${note.id}`}
                          className="border-b border-slate-200 dark:border-slate-700 pb-4 last:border-0 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800 p-3 rounded-lg transition-colors"
                          onClick={() => toggleNoteExpanded(note)}
                        >
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex items-center gap-2">
                              {/* Expand/Collapse indicator */}
                              {expandedNotes.has(note.id) ? (
                                <ChevronDownIcon className="h-4 w-4 text-gray-400" />
                              ) : (
                                <ChevronRightIcon className="h-4 w-4 text-gray-400" />
                              )}
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                {new Date(note.created_at).toLocaleString()}
                              </span>
                              {note.note_type === 'highlight' && (
                                <span
                                  className="px-2 py-0.5 text-xs bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200 rounded"
                                  data-tooltip-id="dhyana-tooltip"
                                  data-tooltip-content="Text highlighted from the document"
                                >
                                  Highlight
                                </span>
                              )}
                              {note.note_type === 'thought' && (
                                <span
                                  className="px-2 py-0.5 text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 rounded"
                                  data-tooltip-id="dhyana-tooltip"
                                  data-tooltip-content="Your personal note or thought"
                                >
                                  Thought
                                </span>
                              )}
                              {note.note_type === 'chat_answer' && (
                                <span
                                  className="px-2 py-0.5 text-xs bg-cyan-100 dark:bg-cyan-900/30 text-cyan-800 dark:text-cyan-200 rounded"
                                  data-tooltip-id="dhyana-tooltip"
                                  data-tooltip-content="Answer generated by AI from chat"
                                >
                                  AI Answer
                                </span>
                              )}

                              {/* Indexing Status Indicator */}
                              {note.is_indexed === false && (
                                <div
                                  className="flex items-center gap-1"
                                  data-tooltip-id="dhyana-tooltip"
                                  data-tooltip-content={
                                    note.indexing_status === 'failed'
                                      ? `Indexing failed: ${note.indexing_error || 'Unknown error'}`
                                      : note.indexing_status === 'running'
                                      ? 'Indexing in progress...'
                                      : 'Indexing queued'
                                  }
                                >
                                  {note.indexing_status === 'failed' && (
                                    <div className="w-2 h-2 rounded-full bg-red-500" />
                                  )}
                                  {(note.indexing_status === 'pending' || note.indexing_status === 'running') && (
                                    <div className="w-2 h-2 rounded-full bg-yellow-500 animate-pulse" />
                                  )}
                                  {note.indexing_status === 'no_task' && (
                                    <div className="w-2 h-2 rounded-full bg-gray-400" />
                                  )}
                                </div>
                              )}
                              {note.is_indexed === true && (
                                <div
                                  className="w-2 h-2 rounded-full bg-green-500"
                                  data-tooltip-id="dhyana-tooltip"
                                  data-tooltip-content="Successfully indexed for search"
                                />
                              )}
                            </div>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                deleteNote(note.id);
                              }}
                              className="p-1 hover:bg-red-100 dark:hover:bg-red-900/30 rounded transition-colors"
                              title="Delete note"
                            >
                              <XMarkIcon className="h-4 w-4 text-red-500" />
                            </button>
                          </div>
                          
                          {/* Show content based on expanded state */}
                          {expandedNotes.has(note.id) ? (
                            // Expanded view - show everything
                            <>
                              {/* User note - rendered as markdown */}
                              {note.user_note && (
                                <div className="mb-2">
                                  <MarkdownRenderer
                                    content={note.user_note}
                                    className="text-sm font-medium"
                                    onNoteClick={scrollToNote}
                                  />
                                </div>
                              )}

                              {/* Content text - rendered as markdown in blockquote */}
                              {note.content_text && (
                                <div className="border-l-4 border-[#64748B] dark:border-[#06B6D4] pl-4 my-2">
                                  <MarkdownRenderer
                                    content={note.content_text}
                                    className="text-sm"
                                    onNoteClick={scrollToNote}
                                  />
                                </div>
                              )}
                              
                              {/* All images in expanded view */}
                              {note.image_urls && note.image_urls.length > 0 && (
                                <div className="grid grid-cols-2 gap-2 mt-2">
                                  {note.image_urls.map((url, idx) => (
                                    <img 
                                      key={idx}
                                      src={url} 
                                      alt={`Note image ${idx + 1}`}
                                      className="w-full rounded-lg border border-gray-200 dark:border-gray-700 cursor-pointer hover:scale-105 transition-transform"
                                      onClick={(e) => handleImageClick(e, note, idx)}
                                      title="Click to view full size"
                                      onError={(e) => {
                                        (e.target as HTMLImageElement).src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KICA8cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iI2VlZSIvPgogIDx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTQiIGZpbGw9IiM5OTkiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5JbWFnZSBmYWlsZWQgdG8gbG9hZDwvdGV4dD4KPC9zdmc+';
                                      }}
                                    />
                                  ))}
                                </div>
                              )}
                              
                              {/* Concept tags - only in expanded view */}
                              {note.concept_tags && note.concept_tags.length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-2">
                                  {note.concept_tags.map((tag, idx) => (
                                    <span key={idx} className="px-2 py-0.5 text-xs bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 rounded">
                                      #{tag}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </>
                          ) : (
                            // Collapsed view - show summary
                            <>
                              {/* Show first 2 lines of user note or content text */}
                              {(note.user_note || note.content_text) && (
                                <div className="mb-2 line-clamp-2">
                                  <MarkdownRenderer
                                    content={note.user_note || note.content_text || ''}
                                    className="text-sm"
                                    onNoteClick={scrollToNote}
                                  />
                                </div>
                              )}
                              
                              {/* Show only first 2 images in collapsed view */}
                              {note.image_urls && note.image_urls.length > 0 && (
                                <div className="flex gap-2 mt-2">
                                  {note.image_urls.slice(0, 2).map((url, idx) => (
                                    <div key={idx} className="relative">
                                      <img 
                                        src={url} 
                                        alt={`Note image ${idx + 1}`}
                                        className="h-16 w-16 object-cover rounded-lg border border-gray-200 dark:border-gray-700"
                                        onError={(e) => {
                                          (e.target as HTMLImageElement).src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KICA8cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iI2VlZSIvPgogIDx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTQiIGZpbGw9IiM5OTkiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5JbWFnZSBmYWlsZWQgdG8gbG9hZDwvdGV4dD4KPC9zdmc+';
                                        }}
                                      />
                                    </div>
                                  ))}
                                  {note.image_urls.length > 2 && (
                                    <div className="h-16 w-16 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center text-sm font-medium text-gray-600 dark:text-gray-300">
                                      +{note.image_urls.length - 2}
                                    </div>
                                  )}
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center text-gray-500 dark:text-gray-400 mt-8">
                      <SparklesIcon className="h-12 w-12 mx-auto mb-4 opacity-20" />
                      <p className="text-base font-medium">Your ThoughtPad</p>
                      <p className="text-sm mt-1">Capture ideas, highlights, and insights</p>
                    </div>
                  )}
                  
                  {/* Show multi-page selections in ThoughtPad when active */}
                  {isMultiPageSelecting && multiPageSelections.length > 0 && (
                    <div className="mt-4 p-3 bg-[#64748B]/10 dark:bg-[#06B6D4]/10 rounded-lg">
                      <h4 className="text-sm font-medium text-[#1F2937] dark:text-[#E2E8F0] mb-2">
                        Multi-page Selection Preview:
                      </h4>
                      <div className="space-y-2 max-h-48 overflow-y-auto text-xs">
                        {multiPageSelections.map((sel, index) => (
                          <div key={index} className="border-l-2 border-[#64748B] dark:border-[#06B6D4] pl-2 pr-1 group">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <span className="font-medium text-[#64748B] dark:text-[#06B6D4]">
                                  Page {sel.pageNumber}:
                                </span>
                                <p className="text-[#6B7280] dark:text-[#94A3B8] mt-1">
                                  {sel.text.substring(0, 100)}
                                  {sel.text.length > 100 && '...'}
                                </p>
                              </div>
        <button
                                onClick={() => {
                                  setMultiPageSelections(prev => prev.filter((_, i) => i !== index));
                                }}
                                className="opacity-0 group-hover:opacity-100 p-1 hover:bg-red-500/10 rounded transition-all"
                                title="Remove this selection"
                              >
                                <XMarkIcon className="h-3 w-3 text-red-500" />
        </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                {/* ThoughtPad Input Area */}
                <div className="border-t border-gray-200 dark:border-gray-700 p-3 space-y-3">
                  {/* Attachment previews */}
                  {thoughtpadAttachments.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {thoughtpadAttachments.map((att, idx) => (
                        <div key={idx} className="relative group">
                          <img 
                            src={att.data_url} 
                            alt="Attachment" 
                            className="h-16 w-16 object-cover rounded-lg border border-gray-200 dark:border-gray-700"
                          />
                          <button
                            onClick={() => setThoughtpadAttachments(prev => prev.filter((_, i) => i !== idx))}
                            className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <XMarkIcon className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Input box */}
                  <div className="relative">
                    <textarea
                      value={thoughtpadInput}
                      onChange={(e) => setThoughtpadInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleSubmitThoughtpadNote();
                        } else if (e.key === 'Escape') {
                          e.preventDefault();
                          setThoughtpadInput('');
                        }
                      }}
                      placeholder="Jot down your thoughts..."
                      className="w-full bg-white dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-lg p-3 pr-40 text-slate-800 dark:text-[#E2E8F0] placeholder-slate-400 dark:placeholder-[#94A3B8] focus:ring-2 focus:ring-cyan-500/30 dark:focus:ring-[#06B6D4]/30 focus:border-cyan-500 dark:focus:border-[#06B6D4] transition resize-none text-sm"
                      rows={5}
                    />
                    <div className="absolute right-3 top-3 flex items-center gap-2">
                      {thoughtpadInput.trim() && (
                        <button
                          onClick={() => setThoughtpadInput('')}
                          className="p-2 hover:bg-red-500/10 rounded-lg transition-colors text-red-500"
                          data-tooltip-id="dhyana-tooltip"
                          data-tooltip-content="Clear text (Escape)"
                        >
                          <XMarkIcon className="h-5 w-5" />
                        </button>
                      )}

                      {/* Snipping Tool Button */}
                      <button
                        onClick={handleStartSnipping}
                        className={`p-2 rounded-lg transition-all ${
                          isSnipping
                            ? 'bg-cyan-500 dark:bg-[#06B6D4] text-white ring-2 ring-cyan-500 dark:ring-[#06B6D4]'
                            : 'hover:bg-cyan-100 dark:hover:bg-[#06B6D4]/10 text-cyan-600 dark:text-[#06B6D4]'
                        }`}
                        data-tooltip-id="dhyana-tooltip"
                        data-tooltip-content={isSnipping ? "Click and drag on PDF to select area (ESC to cancel)" : "Snip PDF area"}
                      >
                        <ScissorsIcon className="h-5 w-5" />
                      </button>

                      <input
                        type="file"
                        id="thoughtpad-file-upload"
                        className="hidden"
                        accept="image/*"
                        multiple
                        onChange={handleThoughtpadFileUpload}
                      />
                      <label
                        htmlFor="thoughtpad-file-upload"
                        className="p-2 hover:bg-cyan-100 dark:hover:bg-[#06B6D4]/10 rounded-lg cursor-pointer transition-colors text-cyan-600 dark:text-[#06B6D4]"
                        data-tooltip-id="dhyana-tooltip"
                        data-tooltip-content="Upload images"
                      >
                        <PaperClipIcon className="h-5 w-5" />
                      </label>

                      <button
                        onClick={handleSubmitThoughtpadNote}
                        disabled={!thoughtpadInput.trim() && thoughtpadAttachments.length === 0}
                        className="bg-[#06B6D4] dark:bg-[#06B6D4] hover:bg-[#0891B2] dark:hover:bg-[#0891B2] text-white rounded-lg p-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                        data-tooltip-id="dhyana-tooltip"
                        data-tooltip-content="Save note (Enter)"
                      >
                        <PaperAirplaneIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                  
                  {/* Clear All button */}
                  <button
                    onClick={async () => {
                      if (window.confirm('Delete all notes on this page? This cannot be undone.')) {
                        try {
                          // Delete all notes for current page
                          const deletePromises = pageNotes.map(note => deleteNote(note.id));
                          await Promise.all(deletePromises);
                          
                          // Refresh notes
                          await fetchNotesForPage(pageNumber);
                        } catch (error) {
                          console.error('Failed to delete notes:', error);
                        }
                      }
                    }}
                    className="text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 px-2 py-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                    disabled={!pageNotes || pageNotes.length === 0}
                  >
                    Clear All
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
        )}


        {/* Floating button to re-open sidebar */}
        {!isSidebarVisible && (
        <button
            onClick={() => {
              setIsSidebarVisible(true);
              setPdfKey(prev => prev + 1); // Force PDF re-render
            }}
            className="fixed right-6 top-24 z-20 p-3.5 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-[#64748B] dark:text-[#06B6D4] rounded-full shadow-lg transition-all duration-200 border border-gray-200 dark:border-gray-600 hover:scale-105"
            title="Open chat sidebar (Cmd/Ctrl + B)"
          >
            <ChatBubbleLeftRightIcon className="h-6 w-6" />
        </button>
        )}

        {/* Add Note Modal */}
        {showAddNoteModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-lg w-full max-h-[80vh] flex flex-col modal-open">
              {/* Modal Header */}
              <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-semibold text-[#1F2937] dark:text-[#E2E8F0]">Add a Note</h3>
        <button
                  onClick={() => {
                    setShowAddNoteModal(false);
                    setNoteText('');
                    setNoteAttachments([]);
                  }}
                  className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                >
                  <XMarkIcon className="h-5 w-5 text-gray-500" />
        </button>
      </div>
              
              {/* Modal Body */}
              <div className="flex-1 overflow-y-auto p-4">
                <textarea
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  placeholder="Type your thoughts here..."
                  className="w-full h-32 p-3 border border-gray-300 dark:border-gray-600 rounded-lg resize-none 
                           bg-white dark:bg-gray-700 text-[#1F2937] dark:text-[#E2E8F0] 
                           placeholder-gray-400 dark:placeholder-gray-500
                           focus:outline-none focus:ring-2 focus:ring-[#64748B] dark:focus:ring-[#06B6D4]"
                  autoFocus
                />
                
                {/* Attachments */}
                {noteAttachments.length > 0 && (
                  <div className="mt-4">
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">Attachments:</p>
                    <div className="flex flex-wrap gap-2">
                      {noteAttachments.map((attachment, index) => (
                        <div key={index} className="relative group">
                          <img 
                            src={attachment.data_url} 
                            alt={`Attachment ${index + 1}`}
                            className="h-20 w-20 object-cover rounded-lg border border-gray-300 dark:border-gray-600"
                          />
                          <button
                            onClick={() => setNoteAttachments(prev => prev.filter((_, i) => i !== index))}
                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <XMarkIcon className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Attachment Button */}
                <div className="mt-4">
                  <label className="flex items-center gap-2 text-sm text-[#64748B] dark:text-[#06B6D4] hover:text-[#4a6669] dark:hover:text-[#0891B2] cursor-pointer">
                    <PhotoIcon className="h-5 w-5" />
                    <span>Attach Image</span>
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={(e) => {
                        const files = Array.from(e.target.files || []);
                        files.forEach(file => {
                          const reader = new FileReader();
                          reader.onload = (event) => {
                            const dataUrl = event.target?.result as string;
                            setNoteAttachments(prev => [...prev, {
                              type: 'image',
                              data_url: dataUrl,
                              file_name: file.name,
                              mime_type: file.type
                            }]);
                          };
                          reader.readAsDataURL(file);
                        });
                      }}
                      className="hidden"
                    />
                  </label>
                </div>
              </div>
              
              {/* Modal Footer */}
              <div className="flex justify-end gap-2 p-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={() => {
                    setShowAddNoteModal(false);
                    setNoteText('');
                    setNoteAttachments([]);
                  }}
                  className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    if (noteText.trim() || noteAttachments.length > 0) {
                      // Prepare image URLs
                      const imageUrls = noteAttachments.map(att => att.data_url!);

                      // Don't await - let it run in background
                      addToThoughtpad(
                        noteText.trim(),
                        'thought', // Note type
                        imageUrls,
                        [], // No concept tags for now
                        {
                          attachment_type: 'upload'
                        }
                      );

                      // Modal close and reset now handled in addToThoughtpad()
                    }
                  }}
                  disabled={!noteText.trim() && noteAttachments.length === 0}
                  className="px-4 py-2 bg-[#64748B] hover:bg-[#4a6669] dark:bg-[#06B6D4] dark:hover:bg-[#0891B2]
                           text-white font-medium rounded-lg transition-colors
                           disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Add to ThoughtPad
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Note Viewer Modal */}
        {showNoteViewerModal && viewingNote && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
              {/* Modal Header */}
              <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Note Details
                </h3>
                <button
                  onClick={() => setShowNoteViewerModal(false)}
                  className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              {/* Modal Body */}
              <div className="flex-1 overflow-y-auto p-4">
                {/* Image Carousel */}
                {viewingNote.image_urls && viewingNote.image_urls.length > 0 && (
                  <div className="mb-6">
                    <div className="relative bg-gray-100 dark:bg-gray-900 rounded-lg overflow-hidden">
                      <img
                        src={viewingNote.image_urls[currentImageIndex]}
                        alt={`Note image ${currentImageIndex + 1}`}
                        className="w-full h-auto max-h-[500px] object-contain mx-auto"
                        onError={(e) => {
                          (e.target as HTMLImageElement).src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KICA8cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iI2VlZSIvPgogIDx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTQiIGZpbGw9IiM5OTkiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5JbWFnZSBmYWlsZWQgdG8gbG9hZDwvdGV4dD4KPC9zdmc+';
                        }}
                      />
                      
                      {/* Navigation arrows */}
                      {viewingNote.image_urls.length > 1 && (
                        <>
                          <button
                            onClick={() => setCurrentImageIndex((prev) => 
                              prev === 0 ? viewingNote.image_urls!.length - 1 : prev - 1
                            )}
                            className="absolute left-2 top-1/2 -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-70 text-white p-2 rounded-full transition-opacity"
                          >
                            <ChevronLeftIcon className="h-6 w-6" />
                          </button>
                          <button
                            onClick={() => setCurrentImageIndex((prev) => 
                              prev === viewingNote.image_urls!.length - 1 ? 0 : prev + 1
                            )}
                            className="absolute right-2 top-1/2 -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-70 text-white p-2 rounded-full transition-opacity"
                          >
                            <ChevronRightIcon className="h-6 w-6" />
                          </button>
                        </>
                      )}
                    </div>
                    
                    {/* Image indicators */}
                    {viewingNote.image_urls.length > 1 && (
                      <div className="flex justify-center gap-2 mt-3">
                        {viewingNote.image_urls.map((_, idx) => (
                          <button
                            key={idx}
                            onClick={() => setCurrentImageIndex(idx)}
                            className={`w-2 h-2 rounded-full transition-colors ${
                              idx === currentImageIndex 
                                ? 'bg-blue-500 dark:bg-blue-400' 
                                : 'bg-gray-300 dark:bg-gray-600'
                            }`}
                            aria-label={`Go to image ${idx + 1}`}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Note Content */}
                <div className="space-y-4">
                  {/* Note metadata */}
                  <div className="flex items-center gap-3 text-sm text-gray-500 dark:text-gray-400">
                    <span>{new Date(viewingNote.created_at).toLocaleString()}</span>
                    {viewingNote.note_type === 'highlight' && (
                      <span className="px-2 py-0.5 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200 rounded">
                        Highlight
                      </span>
                    )}
                    {viewingNote.note_type === 'thought' && (
                      <span className="px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 rounded">
                        Thought
                      </span>
                    )}
                    {viewingNote.note_type === 'chat_answer' && (
                      <span className="px-2 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-200 rounded">
                        AI Answer
                      </span>
                    )}
                  </div>

                  {/* User note */}
                  {viewingNote.user_note && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Note</h4>
                      <p className="text-base text-gray-900 dark:text-gray-100">
                        {viewingNote.user_note}
                      </p>
                    </div>
                  )}

                  {/* Highlighted text */}
                  {viewingNote.content_text && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Highlighted Text</h4>
                      <blockquote className="border-l-4 border-[#64748B] dark:border-[#06B6D4] pl-4 text-gray-600 dark:text-gray-400">
                        {viewingNote.content_text}
                      </blockquote>
                    </div>
                  )}

                  {/* Concept tags */}
                  {viewingNote.concept_tags && viewingNote.concept_tags.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Tags</h4>
                      <div className="flex flex-wrap gap-2">
                        {viewingNote.concept_tags.map((tag, idx) => (
                          <span
                            key={idx}
                            className="px-2 py-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Message Viewer Modal */}
        {showMessageViewerModal && viewingMessage && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
              {/* Modal Header */}
              <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Message Attachments
                </h3>
                <button
                  onClick={() => setShowMessageViewerModal(false)}
                  className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              {/* Modal Body */}
              <div className="flex-1 overflow-y-auto p-4">
                {/* Image Carousel */}
                {viewingMessage.attachments && viewingMessage.attachments.length > 0 && (
                  <div className="mb-6">
                    <div className="relative bg-gray-100 dark:bg-gray-900 rounded-lg overflow-hidden">
                      <img
                        src={viewingMessage.attachments[currentMessageImageIndex].data_url}
                        alt={`Attachment ${currentMessageImageIndex + 1}`}
                        className="w-full h-auto max-h-[500px] object-contain mx-auto"
                      />
                      
                      {/* Navigation arrows */}
                      {viewingMessage.attachments.length > 1 && (
                        <>
                          <button
                            onClick={() => setCurrentMessageImageIndex((prev) => 
                              prev === 0 ? viewingMessage.attachments!.length - 1 : prev - 1
                            )}
                            className="absolute left-2 top-1/2 -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-70 text-white p-2 rounded-full transition-opacity"
                          >
                            <ChevronLeftIcon className="h-6 w-6" />
                          </button>
                          <button
                            onClick={() => setCurrentMessageImageIndex((prev) => 
                              prev === viewingMessage.attachments!.length - 1 ? 0 : prev + 1
                            )}
                            className="absolute right-2 top-1/2 -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-70 text-white p-2 rounded-full transition-opacity"
                          >
                            <ChevronRightIcon className="h-6 w-6" />
                          </button>
                        </>
                      )}
                    </div>
                    
                    {/* Image thumbnails */}
                    {viewingMessage.attachments.length > 1 && (
                      <div className="flex gap-2 mt-4 justify-center">
                        {viewingMessage.attachments.map((attachment, idx) => (
                          <button
                            key={idx}
                            onClick={() => setCurrentMessageImageIndex(idx)}
                            className={`w-16 h-16 rounded overflow-hidden border-2 transition-all ${
                              idx === currentMessageImageIndex
                                ? 'border-[#64748B] dark:border-[#06B6D4]'
                                : 'border-gray-200 dark:border-gray-700 opacity-60 hover:opacity-100'
                            }`}
                          >
                            <img
                              src={attachment.data_url}
                              alt={`Thumbnail ${idx + 1}`}
                              className="w-full h-full object-cover"
                            />
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Message content */}
                {viewingMessage.content_text && (
                  <div className="mb-4">
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Message</h4>
                    <p className="text-base text-gray-900 dark:text-gray-100">
                      {viewingMessage.content_text}
                    </p>
                  </div>
                )}

                {/* Attachment info */}
                {viewingMessage.attachments && viewingMessage.attachments[currentMessageImageIndex] && (
                  <div className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                    <p>Type: {viewingMessage.attachments[currentMessageImageIndex].type}</p>
                    {viewingMessage.attachments[currentMessageImageIndex].page_number && (
                      <p>Page: {viewingMessage.attachments[currentMessageImageIndex].page_number}</p>
                    )}
                    {viewingMessage.attachments[currentMessageImageIndex].file_name && (
                      <p>File: {viewingMessage.attachments[currentMessageImageIndex].file_name}</p>
                    )}
                    <p>Sent: {new Date(viewingMessage.created_at).toLocaleString()}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Image Zoom Modal */}
        {zoomedImageUrl && (
          <div 
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setZoomedImageUrl(null)}
          >
            <div className="relative max-w-[90vw] max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
              <button
                onClick={() => setZoomedImageUrl(null)}
                className="absolute -top-12 right-0 text-white hover:text-gray-300 transition-colors flex items-center gap-2"
              >
                <span className="text-sm">Close</span>
                <XMarkIcon className="h-6 w-6" />
              </button>
              <S3Image
                src={zoomedImageUrl}
                alt="Zoomed view"
                className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl"
              />
            </div>
          </div>
        )}

        {/* Floating Return to Page Button */}
        {showReturnButton && previousPage !== null && (
          <div className="fixed bottom-24 left-1/2 -translate-x-1/2 z-40 animate-slide-up">
            <div className={`flex items-center gap-2 px-4 py-2.5 rounded-full shadow-lg backdrop-blur-md transition-all ${
              isDarkMode 
                ? 'bg-slate-800/90 border border-slate-700/50 text-white' 
                : 'bg-white/90 border border-slate-200/50 text-slate-800'
            }`}>
              <button
                onClick={handleReturnToPreviousPage}
                className="flex items-center gap-2 font-medium hover:opacity-80 transition-opacity"
              >
                <ChevronLeftIcon className="h-4 w-4 text-cyan-500" />
                <span>Return to Page {previousPage}</span>
              </button>
              <button
                onClick={() => {
                  setShowReturnButton(false);
                  setPreviousPage(null);
                  if (returnButtonTimeoutRef.current) {
                    clearTimeout(returnButtonTimeoutRef.current);
                    returnButtonTimeoutRef.current = null;
                  }
                }}
                className={`p-1 rounded-full hover:bg-slate-500/20 transition-colors ${
                  isDarkMode ? 'text-slate-400' : 'text-slate-500'
                }`}
                title="Dismiss"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}

        {/* Floating Return to Chat Button - positioned top-left of PDF area */}
        {returnToChat && (
          <div className="fixed top-20 left-4 z-40 animate-fade-in">
            <div className={`flex items-center gap-2 px-3 py-2 rounded-xl shadow-lg backdrop-blur-md transition-all ${
              isDarkMode 
                ? 'bg-slate-800/95 border border-slate-700/50 text-white' 
                : 'bg-white/95 border border-slate-200 text-slate-800 shadow-md'
            }`}>
              <button
                onClick={handleReturnToChat}
                className="flex items-center gap-2 font-medium hover:opacity-80 transition-opacity text-sm"
              >
                <ChevronLeftIcon className="h-4 w-4 text-cyan-500" />
                <span>Return to Chat</span>
              </button>
              <button
                onClick={() => {
                  setReturnToChat(null);
                  sessionStorage.removeItem('returnToChat');
                }}
                className={`p-1 rounded-full hover:bg-slate-500/20 transition-colors ${
                  isDarkMode ? 'text-slate-400' : 'text-slate-500'
                }`}
                title="Dismiss"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}


        {/* Keyboard Shortcuts Modal */}
        {showKeyboardHelp && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* Backdrop */}
            <div
              className="absolute inset-0 bg-black/70 backdrop-blur-md"
              onClick={() => setShowKeyboardHelp(false)}
            />

            {/* Modal */}
            <div className="relative w-full max-w-2xl bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-gray-200/50 dark:border-gray-700/50 overflow-hidden">
              {/* Header */}
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-white/50 dark:bg-gray-800/50">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                    ⌨️ Keyboard Shortcuts
                  </h3>
                  <button
                    onClick={() => setShowKeyboardHelp(false)}
                    className="p-1.5 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-full transition-colors"
                  >
                    <XMarkIcon className="h-5 w-5 text-gray-700 dark:text-gray-300" />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="p-6 max-h-[70vh] overflow-y-auto">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Navigation */}
                  <div>
                    <h4 className="text-sm font-bold text-gray-700 dark:text-gray-300 mb-3 uppercase tracking-wide">Navigation</h4>
                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600 dark:text-gray-400">Previous Page</span>
                        <kbd className="px-2 py-1 text-xs font-semibold bg-gray-100 dark:bg-gray-800 rounded border border-gray-300 dark:border-gray-600">←</kbd>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600 dark:text-gray-400">Next Page</span>
                        <kbd className="px-2 py-1 text-xs font-semibold bg-gray-100 dark:bg-gray-800 rounded border border-gray-300 dark:border-gray-600">→</kbd>
                      </div>
                    </div>
                  </div>

                  {/* View Controls */}
                  <div>
                    <h4 className="text-sm font-bold text-gray-700 dark:text-gray-300 mb-3 uppercase tracking-wide">View Controls</h4>
                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600 dark:text-gray-400">Zoom In</span>
                        <kbd className="px-2 py-1 text-xs font-semibold bg-gray-100 dark:bg-gray-800 rounded border border-gray-300 dark:border-gray-600">Cmd/Ctrl + +</kbd>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600 dark:text-gray-400">Zoom Out</span>
                        <kbd className="px-2 py-1 text-xs font-semibold bg-gray-100 dark:bg-gray-800 rounded border border-gray-300 dark:border-gray-600">Cmd/Ctrl + -</kbd>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600 dark:text-gray-400">Reset Zoom</span>
                        <kbd className="px-2 py-1 text-xs font-semibold bg-gray-100 dark:bg-gray-800 rounded border border-gray-300 dark:border-gray-600">Cmd/Ctrl + 0</kbd>
                      </div>
                    </div>
                  </div>

                  {/* Features */}
                  <div>
                    <h4 className="text-sm font-bold text-gray-700 dark:text-gray-300 mb-3 uppercase tracking-wide">Features</h4>
                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600 dark:text-gray-400">Toggle Chat Sidebar</span>
                        <kbd className="px-2 py-1 text-xs font-semibold bg-gray-100 dark:bg-gray-800 rounded border border-gray-300 dark:border-gray-600">Cmd/Ctrl + B</kbd>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600 dark:text-gray-400">Toggle TL;DR</span>
                        <kbd className="px-2 py-1 text-xs font-semibold bg-gray-100 dark:bg-gray-800 rounded border border-gray-300 dark:border-gray-600">Cmd/Ctrl + T</kbd>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600 dark:text-gray-400">Add Note</span>
                        <kbd className="px-2 py-1 text-xs font-semibold bg-gray-100 dark:bg-gray-800 rounded border border-gray-300 dark:border-gray-600">Cmd/Ctrl + N</kbd>
                      </div>
                    </div>
                  </div>

                  {/* General */}
                  <div>
                    <h4 className="text-sm font-bold text-gray-700 dark:text-gray-300 mb-3 uppercase tracking-wide">General</h4>
                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600 dark:text-gray-400">Close Modal/Cancel</span>
                        <kbd className="px-2 py-1 text-xs font-semibold bg-gray-100 dark:bg-gray-800 rounded border border-gray-300 dark:border-gray-600">ESC</kbd>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600 dark:text-gray-400">Show This Help</span>
                        <kbd className="px-2 py-1 text-xs font-semibold bg-gray-100 dark:bg-gray-800 rounded border border-gray-300 dark:border-gray-600">?</kbd>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Tip */}
                <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                  <p className="text-sm text-blue-900 dark:text-blue-100">
                    <span className="font-semibold">💡 Pro Tip:</span> Most shortcuts work throughout the app. Try them out to navigate faster!
                  </p>
                  <p className="text-xs text-blue-800 dark:text-blue-200 mt-2">
                    ✂️ Find the snipping tool in the chat input area, next to the attachment button.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Session Progress Toast — removed, replaced by PulseCheckCard inside viewer-container */}
      </main>
      <Tooltip
        id="dhyana-tooltip"
        style={{
          backgroundColor: '#06B6D4',
          color: '#fff',
          borderRadius: '8px',
          padding: '8px 12px',
          fontSize: '12px',
          maxWidth: '200px',
          zIndex: 9999
        }}
      />
      <Tooltip
        id="tour-help-tooltip"
        isOpen={showTourTooltip}
        className="animate-bounce"
        style={{
          backgroundColor: '#06B6D4',
          color: '#fff',
          borderRadius: '8px',
          padding: '8px 12px',
          fontSize: '12px',
          maxWidth: '200px',
          zIndex: 9999
        }}
      />
    </div>
  );
};

export default Dhyana;